import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { ChevronDown, ChevronUp, Sparkles, MessageSquare, Eye, EyeOff, Plus, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { getMetric, getAllMetrics } from "@/lib/metricDefinitions";
import { useMetricDefinitions } from "@/lib/useMetricDefinitions";
import CustomMetricsDialog from "./CustomMetricsDialog";
import { SuggestedCustomMetric } from "./SuggestedCustomMetric";
import { AddMetricDialog } from "./AddMetricDialog";
import { useNavigate, Link } from "react-router-dom";
import { trackOperation } from "@/lib/undoTracking";
import { logAIQuickEntry } from "@/lib/detailedEntryLogger";
import { getLocalizedMetricName } from "@/lib/utils";
import { hapticSoft, hapticSuccess } from "@/utils/haptics";

interface QuickEntryConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestedValues: any;
  userId: string;
  selectedDate?: Date;
  allMetrics: Array<{ key: string; name: string; unit: string; emoji?: string }>;
  metricPreferences: any;
  customMetrics: Array<{ metric_key: string; metric_name: string; metric_emoji: string; metric_unit: string; metric_type?: string; is_inverse?: boolean; is_relative?: boolean }>;
  onEmojiExplosion?: (emojis: string[]) => void;
  onTutorialTrigger?: (metricInfo?: { metricKey: string; metricName: string; isBoolean: boolean }) => void;
  originalInput?: string;
  onFixInChat?: () => void;
  freeFixAvailable?: boolean;
}

export const QuickEntryConfirmDialog = ({
  open,
  onOpenChange,
  suggestedValues,
  userId,
  selectedDate = new Date(),
  allMetrics,
  metricPreferences,
  customMetrics,
  onEmojiExplosion,
  onTutorialTrigger,
  originalInput = "",
  onFixInChat,
  freeFixAvailable = false,
}: QuickEntryConfirmDialogProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [editedValues, setEditedValues] = useState<any>({});
  const [removedMetrics, setRemovedMetrics] = useState<Set<string>>(new Set());

  // Use the centralized hook for metric definitions
  const {
    data: metricDefinitionsData,
    isLoading: isLoadingMetricDefinitions,
    isFetching: isFetchingMetricDefinitions,
    error: metricDefinitionsError,
    refetch: refetchMetricDefinitions
  } = useMetricDefinitions();

  // Ensure metricDefinitions is always an object
  const metricDefinitions = metricDefinitionsData || {};

  // CRITICAL DEBUG: Check the actual structure of the data we received
  if (open && metricDefinitionsData) {
    const sampleKey = Object.keys(metricDefinitionsData)[0];
    const sampleDef = sampleKey ? metricDefinitionsData[sampleKey] : null;
    console.error("[QuickEntryConfirmDialog] Checking metricDefinitionsData structure:", {
      hasData: !!metricDefinitionsData,
      keysCount: Object.keys(metricDefinitionsData).length,
      sampleKey,
      sampleDef,
      sampleDefKeys: sampleDef ? Object.keys(sampleDef) : [],
      hasName: sampleDef ? 'name' in sampleDef : false,
      hasLabel: sampleDef ? 'label' in sampleDef : false,
    });
  }

  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showDisabledMetrics, setShowDisabledMetrics] = useState(false);
  const [healthierExpanded, setHealthierExpanded] = useState(false);
  const [enableHealthierSuggestions, setEnableHealthierSuggestions] = useState<boolean>(true);
  const [customMetricsDialogOpen, setCustomMetricsDialogOpen] = useState(false);
  const [addMetricDialogOpen, setAddMetricDialogOpen] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState<any>(null);
  const [editedSuggestions, setEditedSuggestions] = useState<Map<number, any>>(new Map());

  // State for managing suggested custom metrics removal
  const [removedCustomMetricIndices, setRemovedCustomMetricIndices] = useState<Set<number>>(new Set());

  // State for tracking which optional metrics have been manually enabled
  const [enabledOptionalIndices, setEnabledOptionalIndices] = useState<Set<number>>(new Set());

  // State for storing manually added metrics from the Add Metric dialog
  const [manuallyAddedMetrics, setManuallyAddedMetrics] = useState<Array<{
    key: string;
    name: string;
    emoji: string;
    unit: string;
    type: "numeric" | "boolean";
    goal: number;
    value: number;
    inverse: boolean;
    isAdditive?: boolean;
    isRelative?: boolean;
  }>>([]);

  // State for validation error
  const [validationError, setValidationError] = useState<string | null>(null);

  // State for goal editing
  const [editingGoalFor, setEditingGoalFor] = useState<string | null>(null);
  const [editedGoalValue, setEditedGoalValue] = useState<string>("");

  // State for tracking expanded metric breakdowns (default collapsed)
  const [expandedBreakdowns, setExpandedBreakdowns] = useState<Set<string>>(new Set());

  // State for managing item quantities (for adjusting counts)
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({});

  // State for controlling items breakdown visibility (default collapsed)
  const [itemsBreakdownExpanded, setItemsBreakdownExpanded] = useState(false);

  // Helper function to normalize legacy metric keys
  const normalizeKey = (k: string) => {
    const map: Record<string, string> = {
      appearance: "self_appearance_rating",
      miles: "miles_ran",
      water: "water_gallons",
      body_fat: "body_fat_percentage",
    };

    // Handle custom metric descriptive names by looking them up in customMetrics
    if (k.startsWith('custom_') && !k.match(/^custom_\d+(_value)?$/)) {
      const hasValueSuffix = k.endsWith('_value');
      const descriptiveName = k.replace(/^custom_/, '').replace(/_value$/, '');
      const normalizedDescriptive = descriptiveName.toLowerCase().replace(/[\s-]/g, '_');

      const matchingMetric = customMetrics.find(cm => {
        const metricName = cm.metric_name.toLowerCase().replace(/[\s-]/g, '_');
        return metricName === normalizedDescriptive;
      });

      if (matchingMetric) {
        return hasValueSuffix ? `${matchingMetric.metric_key}_value` : matchingMetric.metric_key;
      }
    }

    // Check if the key matches a custom metric name
    if (!k.startsWith('custom_') && !map[k]) {
      const normalizedKey = k.toLowerCase().replace(/[\s-]/g, '_');
      const matchingMetric = customMetrics.find(cm => {
        const metricName = cm.metric_name.toLowerCase().replace(/[\s-]/g, '_');
        return metricName === normalizedKey;
      });

      if (matchingMetric) {
        return `${matchingMetric.metric_key}_value`;
      }
    }

    return map[k] || k;
  };

  // Recalculate metric values whenever itemQuantities changes
  useEffect(() => {
    const items = suggestedValues?.items || [];
    if (!items || items.length === 0) return;

    const newEditedValues: Record<string, string> = {};

    // Recalculate each metric from the items array
    items.forEach((item: any, itemIndex: number) => {
      if (Array.isArray(item.values)) {
        item.values.forEach((valueEntry: any) => {
          if (valueEntry.key && valueEntry.value_per_item !== undefined) {
            const normalizedKey = normalizeKey(valueEntry.key);

            // Use adjusted quantity from state, or fall back to original quantity
            const quantity = itemQuantities[itemIndex] ?? item.quantity ?? 1;
            const valuePerUnit = typeof valueEntry.value_per_item === 'boolean'
              ? (valueEntry.value_per_item ? 1 : 0)
              : valueEntry.value_per_item;
            const totalValue = valuePerUnit * quantity;

            // Sum up values if multiple items contribute to the same metric
            const currentValue = parseFloat(newEditedValues[normalizedKey] || '0');
            newEditedValues[normalizedKey] = (Math.round((currentValue + totalValue) * 100) / 100).toString();
          }
        });
      }
    });

    // Update editedValues with recalculated values
    setEditedValues(prev => {
      // Preserve any manually edited values for metrics not from items
      const preserved: Record<string, any> = {};
      Object.keys(prev).forEach(key => {
        // Only preserve if this metric isn't being recalculated from items
        if (!newEditedValues[key]) {
          preserved[key] = prev[key];
        }
      });

      return {
        ...preserved,
        ...newEditedValues
      };
    });
  }, [itemQuantities, suggestedValues]);

  // Fetch user goals
  const { data: userGoals } = useQuery({
    queryKey: ["user-goals", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      console.log("[QuickEntryConfirmDialog] User goals loaded:", data);
      return data;
    },
    enabled: open && !!userId,
  });

  // Fetch user profile for plan checking and trial period
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile-created", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("created_at, is_premium")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open && !!userId,
  });

  // Fetch existing entry for selected date
  const entryDate = format(selectedDate, "yyyy-MM-dd");
  const { data: existingEntry } = useQuery({
    queryKey: ["daily-entry", userId, entryDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .eq("entry_date", entryDate)
        .maybeSingle();
      if (error) throw error;
      console.log("[QuickEntryConfirmDialog] Existing entry loaded:", data);
      return data;
    },
    enabled: open && !!userId,
  });

  // Fetch user's healthier suggestions preference and set it in state
  useEffect(() => {
    if (!open || !userId || !metricPreferences) return;

    hapticSoft();

    const fetchHealthierPref = async () => {
      const { data } = await supabase
        .from("metric_preferences")
        .select("enable_healthier_suggestions")
        .eq("user_id", userId)
        .maybeSingle();

      // Default to true if not set
      setEnableHealthierSuggestions(data?.enable_healthier_suggestions !== false);
    };

    fetchHealthierPref();
  }, [open, userId, metricPreferences]);

  // Handler to toggle healthier suggestions preference
  const handleToggleHealthierSuggestions = async (enabled: boolean) => {
    setEnableHealthierSuggestions(enabled);

    try {
      const { error } = await supabase
        .from("metric_preferences")
        .update({ enable_healthier_suggestions: enabled })
        .eq('user_id', userId);

      if (error) {
        console.error("Failed to update healthier suggestions preference:", error);
        toast.error(t('components.quick_entry_confirm.toasts.pref_save_error'));
        // Revert on error
        setEnableHealthierSuggestions(!enabled);
      } else {
        // Invalidate query cache to refetch preferences
        queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] });
      }
    } catch (error) {
      console.error("Error updating preference:", error);
      toast.error(t('components.quick_entry_confirm.toasts.pref_save_error'));
      setEnableHealthierSuggestions(!enabled);
    }
  };

  // Helper to check if a metric is currently enabled by the user
  const isMetricEnabled = (metricKey: string): boolean => {
    // Custom metrics are always considered "enabled" if they exist
    if (metricKey.startsWith('custom_')) {
      return true;
    }

    const nutritionMetrics = metricPreferences?.nutrition_metrics || [];
    const activityMetrics = metricPreferences?.activity_sleep_metrics || [];
    const resultsMetrics = metricPreferences?.results_metrics || [];

    return nutritionMetrics.includes(metricKey) ||
      activityMetrics.includes(metricKey) ||
      resultsMetrics.includes(metricKey);
  };

  // Auto-enable the toggle when dialog opens if no enabled metrics were suggested
  useEffect(() => {
    if (!open || !suggestedValues) return;

    // Clear validation error when dialog opens
    setValidationError(null);

    // CRITICAL: Reset editedValues when dialog opens to prevent stale data from previous entries
    // Without this, values from a previous food entry (e.g., calories, protein) could persist
    // and incorrectly show up in subsequent non-food entries (e.g., skin texture rating)
    setEditedValues({});
    setRemovedMetrics(new Set());
    setRemovedCustomMetricIndices(new Set());
    setEditedSuggestions(new Map());
    setManuallyAddedMetrics([]);
    setEnabledOptionalIndices(new Set());

    // Initialize item quantities from the items array
    const items = suggestedValues.items || [];
    if (Array.isArray(items) && items.length > 0) {
      const initialQuantities: Record<number, number> = {};
      items.forEach((item: any, index: number) => {
        initialQuantities[index] = item.quantity || 1;
      });
      setItemQuantities(initialQuantities);
    }

    const normalizedKeys = Object.keys(suggestedValues)
      .filter(k => k !== 'comment')
      .map(k => normalizeKey(k));

    const hasEnabledMetrics = normalizedKeys.some(key => isMetricEnabled(key));

    // If no enabled metrics were suggested, automatically enable the toggle
    if (!hasEnabledMetrics && normalizedKeys.length > 0) {
      setShowDisabledMetrics(true);
    } else {
      setShowDisabledMetrics(false);
    }
  }, [open, suggestedValues, metricPreferences]);

  // Start progress bar animation - eases toward 90% over ~5 seconds
  const startProgressAnimation = () => {
    setSaveProgress(0);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    const startTime = Date.now();
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      // Ease-out curve: approaches 90% asymptotically over ~5s
      const target = 90 * (1 - Math.exp(-elapsed / 2000));
      setSaveProgress(target);
    }, 50);
  };

  const stopProgressAnimation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const resetProgress = () => {
    stopProgressAnimation();
    setSaveProgress(0);
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => stopProgressAnimation();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    startProgressAnimation();

    // Validate date range: entry_date must be within ±365 days from today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(today);
    oneYearAgo.setDate(today.getDate() - 365);
    const oneYearForward = new Date(today);
    oneYearForward.setDate(today.getDate() + 365);

    const selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);


    if (selectedDateOnly < oneYearAgo || selectedDateOnly > oneYearForward) {
      toast.error(t('components.quick_entry_confirm.toasts.date_range_error'));
      resetProgress();
      setSaving(false);
      return;
    }

    // Track which custom metric slots are boolean (declared at function scope)
    const booleanMetricSlots = new Set<string>();
    // Track which custom metric slots are newly created (declared at function scope)
    const newlyCreatedSlots = new Set<string>();

    try {
      // First, identify which custom metrics need to be created from suggestions
      const customMetricsToCreate = suggestedCustomMetrics
        .map((suggestion, idx) => {
          // Use edited version if available
          const edited = editedSuggestions.get(idx);
          return {
            ...(edited || suggestion),
            originalIndex: idx,
          };
        })
        .filter(suggestion => {
          // Filter out removed metrics
          if (removedCustomMetricIndices.has(suggestion.originalIndex)) return false;

          // Filter out optional metrics that haven't been explicitly enabled
          // EXCEPT when they would be the only metrics to save (auto-include them)
          if (suggestion.is_optional === true && !enabledOptionalIndices.has(suggestion.originalIndex)) {
            // Check if there are any non-optional metrics (built-in or custom) that would be saved
            // Note: suggestedValues contains built-in metrics alongside metadata keys
            const metadataKeys = new Set(['aiComment', 'healthierReason', 'suggestedCustomMetrics', 'items', 
              'references', 'calorie_breakdown', 'inputSummary', 'outputCalculationExplanation']);
            const hasNonOptionalBuiltInMetrics = Object.keys(suggestedValues || {}).some(k => 
              !metadataKeys.has(k) && !removedMetrics.has(normalizeKey(k))
            );
            const hasNonOptionalCustomMetrics = suggestedCustomMetrics.some((s, idx) => 
              idx !== suggestion.originalIndex && 
              !removedCustomMetricIndices.has(idx) && 
              s.is_optional !== true
            );
            const hasManuallyAdded = manuallyAddedMetrics.length > 0;
            
            // Only filter out optional metrics if there are other metrics to save
            if (hasNonOptionalBuiltInMetrics || hasNonOptionalCustomMetrics || hasManuallyAdded) {
              return false;
            }
            // Auto-include optional metric when it's the only thing to save
            console.log(`[QuickEntryConfirmDialog] Auto-including optional custom metric ${suggestion.name} (only metric to save)`);
          }

          return true;
        });

      // Include manually added metrics in the creation list (but exclude existing custom metrics)
      // Existing custom metrics have existingMetricKey set and should only have values logged, not be created
      const newManuallyAddedMetrics = manuallyAddedMetrics.filter(m => !(m as any).existingMetricKey);
      const allMetricsToCreate = [...customMetricsToCreate, ...newManuallyAddedMetrics];

      // Check if we need to create custom metrics and validate plan limits
      if (allMetricsToCreate.length > 0) {
        const isPremium = userProfile?.is_premium ?? false;
        const currentCustomMetricCount = customMetrics.length;

        // Calculate days since account creation for trial period check
        const createdAt = userProfile?.created_at ? new Date(userProfile.created_at) : null;
        const daysSinceCreation = createdAt
          ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
          : 999; // Default to high number if unknown (assume past trial)

        const isInTrialPeriod = daysSinceCreation < 7;

        // Determine max allowed based on premium status and trial period
        let maxAllowed: number;
        if (isPremium) {
          maxAllowed = 10; // Premium users always get 10
        } else if (isInTrialPeriod) {
          maxAllowed = 10; // Trial period: all users get 10
        } else {
          maxAllowed = 0; // After trial: non-premium gets 0
        }

        const totalAfterCreation = currentCustomMetricCount + allMetricsToCreate.length;

        console.log('[QuickEntryConfirmDialog] Custom metric validation:', {
          isPremium,
          isInTrialPeriod,
          daysSinceCreation,
          currentCount: currentCustomMetricCount,
          toCreateCount: allMetricsToCreate.length,
          totalAfter: totalAfterCreation,
          maxAllowed
        });

        // Validate plan limits
        if (totalAfterCreation > maxAllowed) {
          let message: string;
          if (isPremium) {
            message = `You can only have up to 10 custom metrics. You currently have ${currentCustomMetricCount}, and you're trying to add ${allMetricsToCreate.length} more.`;
          } else if (isInTrialPeriod) {
            message = `You can create up to 10 custom metrics during your first 7 days (${currentCustomMetricCount} currently used). You're trying to add ${allMetricsToCreate.length} more.`;
          } else {
            message = `Custom metrics require Premium after the 7-day trial. Upgrade to Premium for up to 10 custom metrics!`;
          }

          setValidationError(message);
          resetProgress();
          setSaving(false);
          return;
        }

        // Create the custom metrics
        const MAX_CUSTOM_METRICS = isPremium || isInTrialPeriod ? 10 : 0;
        const availableSlots = Array.from({ length: MAX_CUSTOM_METRICS }, (_, i) => `custom_${i + 1}`);
        const usedSlots = new Set(customMetrics.map(m => m.metric_key));

        // Map to track which suggestion got which slot
        const suggestionToSlotMap = new Map<number, string>();

        for (const metric of allMetricsToCreate) {
          const nextAvailableSlot = availableSlots.find(slot => !usedSlots.has(slot));

          if (!nextAvailableSlot) {
            toast.error(t('components.quick_entry_confirm.toasts.slots_full'));
            continue;
          }

          // Create the custom metric
          const metricName = metric.name.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 23);
          const { error: metricError } = await supabase
            .from("custom_metrics")
            .insert({
              user_id: userId,
              metric_key: nextAvailableSlot,
              metric_name: metricName,
              metric_emoji: metric.emoji,
              metric_unit: metric.type === "numeric" ? metric.unit.slice(0, 20) : "",
              metric_type: metric.type,
              is_inverse: metric.inverse,
              is_additive: metric.is_additive ?? true,
              is_relative: metric.is_relative ?? false,
            });

          if (metricError) {
            console.error('[QuickEntryConfirmDialog] Error creating custom metric:', metricError);
            const errorMessage = metricError.message || metricError.details || 'Unknown error';
            toast.error(t('components.quick_entry_confirm.toasts.create_error', { name: metric.name, error: errorMessage }));
            continue;
          }

          // Send push notification to followers about new graph creation
          // This bypasses notification settings and sends to all followers
          try {
            await supabase.functions.invoke('send-graph-creation-notification', {
              body: {
                userId: userId,
                graphName: metricName,
              }
            });
          } catch (notificationError) {
            console.error('[QuickEntryConfirmDialog] Failed to send graph creation notification:', notificationError);
            // Don't fail the whole operation if notification fails
          }

          // Save goal if numeric and NOT relative (relative metrics don't have goals)
          if (metric.type === "numeric" && !metric.is_relative) {
            const goalKey = `${nextAvailableSlot}_goal`;
            const { error: goalError } = await supabase
              .from("user_goals")
              .upsert({
                user_id: userId,
                [goalKey]: parseFloat(String(metric.goal)) || 100,
              }, {
                onConflict: "user_id",
              });

            if (goalError) {
              console.error('[QuickEntryConfirmDialog] Error saving goal:', goalError);
            }
          }

          // Save colors if not inverse
          if (!metric.inverse) {
            // Default hue (green)
            const hue = 142;
            const { data: colorPrefs } = await supabase
              .from("user_chart_preferences")
              .select("metric_colors")
              .eq("user_id", userId)
              .maybeSingle();

            const existingColors = (colorPrefs?.metric_colors as Record<string, number>) || {};
            const updatedColors = {
              ...existingColors,
              [nextAvailableSlot]: hue,
            };

            await supabase.from("user_chart_preferences").upsert({
              user_id: userId,
              metric_colors: updatedColors,
            }, {
              onConflict: "user_id",
            });
          }

          // Track which slot this suggestion got (if it has an originalIndex)
          if ('originalIndex' in metric && typeof metric.originalIndex === 'number') {
            suggestionToSlotMap.set(metric.originalIndex, nextAvailableSlot);
          } else {
            // For manually added metrics, store using the temp key
            suggestionToSlotMap.set(-1 - manuallyAddedMetrics.indexOf(metric), nextAvailableSlot);
          }

          // Track that this slot was newly created
          newlyCreatedSlots.add(`${nextAvailableSlot}_value`);

          // Track if this is a boolean metric
          if (metric.type === "boolean") {
            booleanMetricSlots.add(`${nextAvailableSlot}_value`);
          }

          // Add to used slots
          usedSlots.add(nextAvailableSlot);

          console.log(`[QuickEntryConfirmDialog] Created custom metric: ${metric.name} at ${nextAvailableSlot} with value ${metric.value}`);
        }

        // Invalidate queries to refresh custom metrics list
        queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] });
        queryClient.invalidateQueries({ queryKey: ["chart-preferences"] });
        queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });

        // Now we need to add the values for the newly created metrics
        // Replace temporary keys with actual slot keys in editedValues
        const updatedEditedValues = { ...editedValues };
        suggestionToSlotMap.forEach((slot, originalIndex) => {
          if (originalIndex >= 0) {
            // This is a suggested custom metric
            const tempKey = `__custom_suggestion_${originalIndex}`;
            if (updatedEditedValues[tempKey] !== undefined) {
              updatedEditedValues[`${slot}_value`] = updatedEditedValues[tempKey];
              delete updatedEditedValues[tempKey];
            } else {
              // Use the default value from the suggestion (check edited version first)
              const edited = editedSuggestions.get(originalIndex);
              const suggestion = edited || suggestedCustomMetrics[originalIndex];
              if (suggestion) {
                // Use the suggestion value for both boolean and numeric metrics
                updatedEditedValues[`${slot}_value`] = suggestion.value;
              }
            }
          } else {
            // This is a manually added metric
            const manualIndex = -originalIndex - 1;
            const manualMetric = manuallyAddedMetrics[manualIndex];
            const tempKey = `__manual_${manualIndex}`;
            if (updatedEditedValues[tempKey] !== undefined) {
              updatedEditedValues[`${slot}_value`] = updatedEditedValues[tempKey];
              delete updatedEditedValues[tempKey];
            } else if (manualMetric) {
              // Use the default value from the manually added metric
              updatedEditedValues[`${slot}_value`] = manualMetric.value;
            }
          }
        });

        console.log('[QuickEntryConfirmDialog] Updated edited values after mapping:', updatedEditedValues);

        // Update editedValues for use in the rest of the save logic
        Object.assign(editedValues, updatedEditedValues);
      }

      const entryDate = format(selectedDate, "yyyy-MM-dd");

      // Get existing entry for selected date
      const { data: existingEntry, error: fetchError } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .eq("entry_date", entryDate)
        .maybeSingle();

      if (fetchError) {
        console.error("Error fetching existing entry:", fetchError);
        throw fetchError;
      }

      const valuesToSave: any = {};
      const metricsToEnable: string[] = [];

      // Normalize keys from suggestion (using component-level normalizeKey)
      const normalizedSuggested: Record<string, any> = Object.fromEntries(
        Object.entries(suggestedValues || {})
          .filter(([k]) => k !== 'aiComment' && k !== 'healthierReason' &&
            k !== 'suggestedCustomMetrics' && k !== 'items')
          .map(([k, v]) => [normalizeKey(k), v])
      );

      // Extract metric values from items array and add them to normalizedSuggested
      // This handles cases where AI only returns metrics in the items array
      const items = suggestedValues?.items || [];
      if (Array.isArray(items) && items.length > 0) {
        items.forEach((item: any) => {
          if (Array.isArray(item.values)) {
            item.values.forEach((valueEntry: any) => {
              if (valueEntry.key && valueEntry.value_per_item !== undefined) {
                const normalizedKey = normalizeKey(valueEntry.key);

                // Skip if this metric was already provided in top-level suggestedValues
                // to avoid double-counting
                if (normalizedSuggested[normalizedKey] !== undefined) {
                  console.log(`[QuickEntryConfirmDialog] Skipping item extraction for ${valueEntry.key} -> ${normalizedKey} (already in suggestedValues)`);
                  return;
                }

                const quantity = item.quantity || 1;
                const valuePerUnit = typeof valueEntry.value_per_item === 'boolean'
                  ? (valueEntry.value_per_item ? 1 : 0)
                  : valueEntry.value_per_item;
                const totalValue = valuePerUnit * quantity;

                normalizedSuggested[normalizedKey] = totalValue;
                console.log(`[QuickEntryConfirmDialog] Extracted from items for save: ${valueEntry.key} -> ${normalizedKey} = ${totalValue}`);
              }
            });
          }
        });
      }

      // Column type helpers based on schema
      const booleanKeys = new Set(["ate_healthy", "low_sugars", "fasted"]);
      const integerKeys = new Set([
        "calories",
        "protein",
        "carbs",
        "fiber",
        "mood",
        "self_appearance_rating",
        "github_commits",
      ]);
      // Metrics that should be set (replaced) rather than added
      const replaceKeys = new Set(["mood", "self_appearance_rating", "body_weight", "body_fat_percentage"]);

      // Build the update object - ADD values to existing ones for all suggested metrics (excluding removed ones)
      Object.keys(normalizedSuggested).forEach((key) => {
        if (removedMetrics.has(key)) return; // Skip removed metrics

        // Skip custom metrics here - they'll be processed separately below
        if (key.startsWith('custom_') && key.endsWith('_value')) {
          return;
        }

        // Skip invalid metric keys that aren't in metricDefinitions (e.g., AI suggested custom metric names)
        if (!metricDefinitions[key]) {
          console.log(`[QuickEntryConfirmDialog] Skipping invalid built-in metric key: ${key}`);
          return;
        }

        // Skip metrics that are not enabled if the toggle is off
        if (!showDisabledMetrics && !isMetricEnabled(key)) {
          return;
        }

        const sourceVal = editedValues[key] !== undefined ? editedValues[key] : normalizedSuggested[key];
        if (sourceVal === null || sourceVal === undefined || sourceVal === "") return;

        // Handle booleans: set true if suggested as true/1; don't force false
        if (booleanKeys.has(key)) {
          const truthy = (typeof sourceVal === "boolean" && sourceVal) || Number(sourceVal) > 0 || String(sourceVal).toLowerCase() === "true";
          if (truthy) {
            valuesToSave[key] = true;
            metricsToEnable.push(key);
          }
          return;
        }

        const numValue = Number(sourceVal);
        if (!Number.isFinite(numValue)) return; // skip invalid numbers

        let finalValue: number;

        // For replace-type metrics (mood, appearance, weight, body fat), use the new value directly
        if (replaceKeys.has(key)) {
          finalValue = numValue;
        } else {
          // For additive metrics, add to existing value
          const existingRaw = existingEntry?.[key as keyof typeof existingEntry];
          const existingNum = Number.isFinite(Number(existingRaw)) ? Number(existingRaw) : 0;
          finalValue = existingNum + numValue;
        }

        // Coerce to integer for integer columns
        if (integerKeys.has(key)) {
          finalValue = Math.round(finalValue);
        }

        // Validate mood and appearance are within 1-10 range
        if (key === "mood" || key === "self_appearance_rating") {
          finalValue = Math.max(1, Math.min(10, finalValue));
        }

        valuesToSave[key] = finalValue;
        metricsToEnable.push(key);
      });

      // Process built-in metrics that were added manually (in editedValues but not in normalizedSuggested)
      Object.keys(editedValues).forEach((key) => {
        // Skip if already processed
        if (normalizedSuggested[key] !== undefined) return;
        // Skip if removed
        if (removedMetrics.has(key)) return;
        // Skip custom metrics (will be handled below)
        if (key.startsWith('custom_') || key.startsWith('__manual') || key.startsWith('__custom_suggestion')) return;
        // Skip if not a valid metric definition
        if (!metricDefinitions[key]) return;

        const sourceVal = editedValues[key];
        if (sourceVal === null || sourceVal === undefined || sourceVal === "") return;

        // Handle booleans
        if (booleanKeys.has(key)) {
          const truthy = (typeof sourceVal === "boolean" && sourceVal) || Number(sourceVal) > 0 || String(sourceVal).toLowerCase() === "true";
          if (truthy) {
            valuesToSave[key] = true;
            metricsToEnable.push(key);
          }
          return;
        }

        const numValue = Number(sourceVal);
        if (!Number.isFinite(numValue)) return;

        let finalValue: number;

        if (replaceKeys.has(key)) {
          finalValue = numValue;
        } else {
          const existingRaw = existingEntry?.[key as keyof typeof existingEntry];
          const existingNum = Number.isFinite(Number(existingRaw)) ? Number(existingRaw) : 0;
          finalValue = existingNum + numValue;
        }

        if (integerKeys.has(key)) {
          finalValue = Math.round(finalValue);
        }

        if (key === "mood" || key === "self_appearance_rating") {
          finalValue = Math.max(1, Math.min(10, finalValue));
        }

        valuesToSave[key] = finalValue;
        metricsToEnable.push(key);
      });

      // Process manually added existing custom metrics
      manuallyAddedMetrics.forEach((metric) => {
        if ((metric as any).existingMetricKey) {
          // This is an existing custom metric being added
          const metricKey = (metric as any).existingMetricKey;
          const valueKey = `${metricKey}_value`;
          const manualKey = metric.key;

          // Get the value from editedValues using the manual key
          const sourceVal = editedValues[manualKey];

          console.log(`[QuickEntryConfirmDialog] Processing existing custom metric for save:`, {
            metricName: metric.name,
            metricKey,
            valueKey,
            manualKey,
            sourceVal,
            editedValuesKeys: Object.keys(editedValues),
          });

          if (sourceVal === null || sourceVal === undefined || sourceVal === "") return;

          const isBoolean = metric.type === "boolean";

          if (isBoolean) {
            let boolValue: boolean;
            if (typeof sourceVal === 'string') {
              const lowerVal = sourceVal.toLowerCase();
              boolValue = lowerVal === 'true' || lowerVal === '1';
            } else {
              boolValue = Boolean(Number(sourceVal));
            }
            valuesToSave[valueKey] = boolValue ? 1 : 0;
            console.log(`[QuickEntryConfirmDialog] Saving boolean custom metric: ${valueKey} = ${valuesToSave[valueKey]}`);
          } else {
            const numValue = Number(sourceVal);
            if (!Number.isFinite(numValue)) return;

            // Check if this custom metric is additive or absolute
            const customMetricKey = valueKey.replace('_value', '');
            const customMetric = customMetrics.find(cm => cm.metric_key === customMetricKey);
            const isAdditive = !customMetric || (customMetric as any).is_additive !== false; // default to true

            if (isAdditive) {
              const existingRaw = existingEntry?.[valueKey as keyof typeof existingEntry];
              const existingNum = Number.isFinite(Number(existingRaw)) ? Number(existingRaw) : 0;
              valuesToSave[valueKey] = existingNum + numValue;
              console.log(`[QuickEntryConfirmDialog] Saving additive custom metric: ${valueKey} = ${valuesToSave[valueKey]} (existing: ${existingNum}, added: ${numValue})`);
            } else {
              valuesToSave[valueKey] = numValue;
              console.log(`[QuickEntryConfirmDialog] Saving absolute custom metric: ${valueKey} = ${valuesToSave[valueKey]} (replaced)`);
            }
          }
        }
      });

      // Process custom metric values from both editedValues AND normalizedSuggested
      const customMetricKeys = new Set([
        ...Object.keys(editedValues).filter(k => k.startsWith('custom_') && k.endsWith('_value')),
        ...Object.keys(normalizedSuggested).filter(k => k.startsWith('custom_') && k.endsWith('_value'))
      ]);

      customMetricKeys.forEach((key) => {
        // Skip if this metric was removed
        if (removedMetrics.has(key)) return;

        // Get the value from editedValues first, then fall back to normalizedSuggested
        const sourceVal = editedValues[key] !== undefined ? editedValues[key] : normalizedSuggested[key];
        console.log(`[QuickEntryConfirmDialog] Processing custom metric ${key}: sourceVal=${sourceVal}, type=${typeof sourceVal}`);
        if (sourceVal === null || sourceVal === undefined || sourceVal === "") return;

        // Check if this is a boolean metric (from newly created suggestions OR existing custom metrics)
        const isBooleanFromSlots = typeof booleanMetricSlots !== 'undefined' && booleanMetricSlots.has(key);

        // Also check if it's an existing boolean custom metric
        let isBooleanExisting = false;
        if (key.startsWith('custom_') && key.endsWith('_value')) {
          const metricKey = key.replace('_value', '');
          const customMetric = customMetrics.find(cm => cm.metric_key === metricKey);
          if (customMetric && (customMetric as any).metric_type === "boolean") {
            isBooleanExisting = true;
          }
        }

        const isBoolean = isBooleanFromSlots || isBooleanExisting;

        // Check if this custom metric is additive or absolute (defaults to additive)
        let isAdditive = true;
        if (key.startsWith('custom_') && key.endsWith('_value')) {
          const metricKey = key.replace('_value', '');
          const customMetric = customMetrics.find(cm => cm.metric_key === metricKey);
          if (customMetric && (customMetric as any).is_additive === false) {
            isAdditive = false;
          }
        }

        if (isBoolean) {
          // For boolean custom metrics, convert to boolean
          // Handle various input formats: "1"/"0", 1/0, true/false, "true"/"false"
          let boolValue: boolean;
          if (typeof sourceVal === 'string') {
            // Handle string inputs
            const lowerVal = sourceVal.toLowerCase();
            if (lowerVal === 'true' || lowerVal === '1') {
              boolValue = true;
            } else {
              boolValue = false;
            }
          } else {
            // Handle boolean or number inputs
            boolValue = Boolean(Number(sourceVal));
          }
          console.log(`[QuickEntryConfirmDialog] Processing boolean metric ${key}: sourceVal=${sourceVal}, type=${typeof sourceVal}, boolValue=${boolValue}, isNewlyCreated=${typeof newlyCreatedSlots !== 'undefined' && newlyCreatedSlots.has(key)}`);
          // For boolean metrics, always save the value (0 or 1), not just when true
          // Store as number (0 or 1) not boolean, since DB expects numeric type
          valuesToSave[key] = boolValue ? 1 : 0;
          console.log(`[QuickEntryConfirmDialog] Saved boolean metric ${key} = ${boolValue ? 1 : 0}`);
          return;
        }

        // For numeric custom metrics
        const numValue = Number(sourceVal);
        if (!Number.isFinite(numValue)) return;

        // Check if this is a newly created metric
        const isNewlyCreated = typeof newlyCreatedSlots !== 'undefined' && newlyCreatedSlots.has(key);

        if (isNewlyCreated || !isAdditive) {
          // For newly created metrics OR non-additive metrics, SET the value directly (don't add)
          valuesToSave[key] = numValue;
          console.log(`[QuickEntryConfirmDialog] Saving ${isNewlyCreated ? 'new' : 'absolute'} custom metric: ${key} = ${numValue}`);
        } else {
          // For existing additive custom metrics, add to existing value
          const existingRaw = existingEntry?.[key as keyof typeof existingEntry];
          const existingNum = Number.isFinite(Number(existingRaw)) ? Number(existingRaw) : 0;
          const finalValue = existingNum + numValue;
          valuesToSave[key] = finalValue;
        }

        // Custom metrics are always "enabled" so no need to add to metricsToEnable
      });

      console.log("Values to save:", valuesToSave);
      console.log("Boolean metric slots:", Array.from(booleanMetricSlots));
      console.log("Newly created slots:", Array.from(newlyCreatedSlots));
      console.log("Edited values keys:", Object.keys(editedValues));

      // If newly created custom metrics exist but have no values yet, ensure they get value 0
      // This prevents "No Values to Save" when custom metric was created but value wasn't entered
      if (Object.keys(valuesToSave).length === 0 && newlyCreatedSlots.size > 0) {
        console.log('[QuickEntryConfirmDialog] No explicit values but custom metrics were created - saving with default values');
        newlyCreatedSlots.forEach(slotKey => {
          if (!removedMetrics.has(slotKey)) {
            const isBool = booleanMetricSlots.has(slotKey);
            valuesToSave[slotKey] = isBool ? 0 : 0;
            console.log(`[QuickEntryConfirmDialog] Auto-saving newly created metric ${slotKey} with default value 0`);
          }
        });
      }

      if (Object.keys(valuesToSave).length === 0) {
        // Detailed logging to help diagnose why no values were saved
        console.error('[QuickEntryConfirmDialog] No values to save - debugging info:', {
          suggestedValuesKeys: Object.keys(normalizedSuggested),
          editedValuesKeys: Object.keys(editedValues),
          removedMetrics: Array.from(removedMetrics),
          showDisabledMetrics,
          customMetricsToCreateCount: allMetricsToCreate.length,
          booleanMetricSlotsCount: booleanMetricSlots.size,
          newlyCreatedSlotsCount: newlyCreatedSlots.size,
          customMetricKeys: Object.keys(editedValues).filter(k => k.startsWith('custom_')),
          normalizedSuggestedSample: Object.entries(normalizedSuggested).slice(0, 3)
        });
        toast.error(t('notifications.toast.no_values_to_save_this_might_be_due_to_all_suggest'));
        resetProgress();
        setSaving(false);
        return;
      }

      // Enable metrics that aren't already enabled (ALWAYS do this for all suggested metrics)
      console.log("Metrics to enable:", metricsToEnable);

      // Fetch current metric preferences
      const { data: metricPrefs, error: prefsError } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (prefsError && prefsError.code !== 'PGRST116') {
        console.error("Error fetching metric preferences:", prefsError);
      }

      const currentNutrition: string[] = metricPrefs?.nutrition_metrics || [];
      const currentActivity: string[] = metricPrefs?.activity_sleep_metrics || [];
      const currentResults: string[] = metricPrefs?.results_metrics || [];

      const newNutrition: string[] = [...currentNutrition];
      const newActivity: string[] = [...currentActivity];
      const newResults: string[] = [...currentResults];

      let metricsAdded = false;

      // Add ALL metrics from metricsToEnable to their respective preference arrays based on category
      // Use the already-loaded metricDefinitions to determine category
      metricsToEnable.forEach(key => {
        const metricDef = metricDefinitions[key];
        if (!metricDef) {
          console.warn(`[QuickEntryConfirmDialog] No definition found for metric: ${key}`);
          return;
        }

        const category = metricDef.category;

        if (category === 'nutrition' && !newNutrition.includes(key)) {
          console.log(`Enabling nutrition metric: ${key}`);
          newNutrition.push(key);
          metricsAdded = true;
        } else if (category === 'activity_sleep' && !newActivity.includes(key)) {
          console.log(`Enabling activity metric: ${key}`);
          newActivity.push(key);
          metricsAdded = true;
        } else if ((category === 'results' || category === 'other') && !newResults.includes(key)) {
          // Both 'results' and 'other' categories go to results_metrics for display
          console.log(`Enabling results metric: ${key} (category: ${category})`);
          newResults.push(key);
          metricsAdded = true;
        }
      });

      // Always upsert metric preferences to ensure they exist
      const { error: updatePrefsError } = await supabase
        .from("metric_preferences")
        .upsert({
          user_id: userId,
          nutrition_metrics: newNutrition,
          activity_sleep_metrics: newActivity,
          results_metrics: newResults,
        });

      if (updatePrefsError) {
        console.error("Error updating metric preferences:", updatePrefsError);
        toast.error(t('notifications.toast.failed_to_enable_metrics'));
      } else {
        if (metricsAdded) {
          console.log("Successfully enabled new metrics");
        }
        // Invalidate all metric-related queries to ensure UI updates everywhere
        queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] });
        queryClient.invalidateQueries({ queryKey: ["metric-preferences"] }); // Also invalidate without userId for any generic queries
        queryClient.invalidateQueries({ queryKey: ["enabled-metrics"] }); // Invalidate enabled metrics cache
      }

      // Single upsert to handle create/update reliably
      // Insert or update depending on existence (avoid on_conflict requirement)
      let mutationError: any = null;
      let affected = 0;
      if (existingEntry) {
        // For UPDATE, don't include user_id and entry_date in the payload (they're used in WHERE clause)
        const { error, data } = await supabase
          .from("daily_entries")
          .update(valuesToSave)
          .eq("user_id", userId)
          .eq("entry_date", entryDate)
          .select();
        mutationError = error;
        affected = Array.isArray(data) ? data.length : data ? 1 : 0;
      } else {
        // For INSERT, include user_id and entry_date
        const insertPayload = {
          user_id: userId,
          entry_date: entryDate,
          ...valuesToSave,
        };
        const { error, data } = await supabase
          .from("daily_entries")
          .insert(insertPayload)
          .select();
        mutationError = error;
        affected = Array.isArray(data) ? data.length : data ? 1 : 0;
      }

      if (mutationError || affected === 0) {
        console.error("Save error or no rows affected:", mutationError, affected);
        throw mutationError || new Error("No rows affected");
      }

      // Log detailed entries for each metric (silently, non-blocking)
      Object.entries(valuesToSave).forEach(([metricKey, newValue]) => {
        // Get metric info for display
        let metricName = "";
        let metricEmoji = "";
        let metricUnit = "";

        // Check if it's a custom metric
        if (metricKey.startsWith('custom_')) {
          const metricKeyWithoutValue = metricKey.replace('_value', '');
          const customMetric = customMetrics.find(cm => cm.metric_key === metricKeyWithoutValue);
          if (customMetric) {
            metricName = customMetric.metric_name;
            metricEmoji = customMetric.metric_emoji;
            metricUnit = customMetric.metric_unit || "";
          }
        } else {
          // Built-in metric
          const metricDef = metricDefinitions[metricKey];
          if (metricDef) {
            metricName = metricDef.name || metricDef.label || metricKey;
            metricEmoji = metricDef.emoji || "";
            metricUnit = metricDef.unit || "";
          }
        }

        // Check if this metric has an AI breakdown (item-by-item) from the items array
        const items = suggestedValues?.items || [];
        const itemsWithMetric = items.filter((item: any) => {
          // Check if item has this metric in its values array
          if (Array.isArray(item.values)) {
            return item.values.some((v: any) => {
              const normalizedValueKey = normalizeKey(v.key);
              return normalizedValueKey === metricKey && (v.value_per_item > 0 || v.value_per_item === true);
            });
          }
          // Fallback: check direct property
          return item[metricKey] !== undefined && item[metricKey] !== null && item[metricKey] !== 0;
        });

        if (itemsWithMetric.length > 0) {
          // Log each item in the breakdown
          itemsWithMetric.forEach((item: any) => {
            const quantity = item.quantity || 1;

            // Get value_per_item from the values array
            let valuePerUnit = null;
            if (Array.isArray(item.values)) {
              const valueEntry = item.values.find((v: any) => {
                const normalizedValueKey = normalizeKey(v.key);
                return normalizedValueKey === metricKey;
              });
              if (valueEntry) {
                valuePerUnit = valueEntry.value_per_item;
              }
            }
            // Fallback to direct property
            if (valuePerUnit === null && item[metricKey] !== undefined) {
              valuePerUnit = item[metricKey];
            }

            const numericValue = typeof valuePerUnit === 'boolean' ? (valuePerUnit ? 1 : 0) : valuePerUnit;
            const total = numericValue * quantity;

            logAIQuickEntry({
              userId,
              entryDate,
              metricKey,
              value: total,
              metricName,
              metricEmoji,
              unit: metricUnit,
              item: item.name,
              operation: "add",
            });
          });
        } else {
          // No breakdown, log a single entry
          logAIQuickEntry({
            userId,
            entryDate,
            metricKey,
            value: typeof newValue === 'number' ? newValue : Number(newValue),
            metricName,
            metricEmoji,
            unit: metricUnit,
          });
        }
      });

      // Track operation for undo
      // Build the list of metric updates
      const metricUpdates = Object.entries(valuesToSave).map(([metricKey, newValue]) => {
        const previousValue = existingEntry?.[metricKey] ?? null;
        return {
          metricKey,
          previousValue: previousValue !== null ? Number(previousValue) : null,
          newValue: Number(newValue),
        };
      });

      // Track custom metrics that were created (only if any were created)
      if (allMetricsToCreate.length > 0) {
        // Since we created multiple metrics, track only the first one for undo
        // (undoing will delete all created in this operation)
        const firstCreatedSlot = Array.from(newlyCreatedSlots)[0]?.replace('_value', '');
        if (firstCreatedSlot) {
          trackOperation({
            type: 'custom_metric_created',
            userId,
            entryDate,
            customMetricCreated: {
              metricKey: firstCreatedSlot,
              metricId: firstCreatedSlot, // We'll use the slot as ID
            },
          });
        }
      } else if (metricsAdded) {
        // Track built-in metrics that were enabled
        // Find the first enabled metric for undo tracking
        const firstEnabledMetric = metricsToEnable[0];
        if (firstEnabledMetric) {
          const metricDef = metricDefinitions[firstEnabledMetric];
          let category: 'nutrition_metrics' | 'activity_sleep_metrics' | 'results_metrics' = 'nutrition_metrics';

          // Determine which preference array this metric was added to based on its category
          if (metricDef?.category === 'activity_sleep') {
            category = 'activity_sleep_metrics';
          } else if (metricDef?.category === 'results' || metricDef?.category === 'other') {
            category = 'results_metrics';
          }

          trackOperation({
            type: 'builtin_metric_enabled',
            userId,
            entryDate,
            builtinMetricEnabled: {
              metricKey: firstEnabledMetric,
              category,
            },
          });
        }
      } else if (metricUpdates.length > 0) {
        // Track value updates only if no metrics were created or enabled
        trackOperation({
          type: 'metric_value_update',
          userId,
          entryDate,
          metricUpdates,
        });
      }

      // Mark that user has successfully used the AI input feature
      localStorage.setItem('ai_input_used', 'true');

      // Collect emojis from the metrics that were logged for explosion animation
      const loggedEmojis: string[] = [];
      Object.keys(valuesToSave).forEach((key) => {
        // Check if it's a custom metric
        if (key.startsWith('custom_')) {
          // Remove the _value suffix if present to get the metric key
          const metricKey = key.replace('_value', '');
          const customMetric = customMetrics.find(cm => cm.metric_key === metricKey);
          if (customMetric?.metric_emoji) {
            loggedEmojis.push(customMetric.metric_emoji);
          }
        } else {
          // Check built-in metrics
          const metricDef = metricDefinitions[key];
          if (metricDef?.emoji) {
            loggedEmojis.push(metricDef.emoji);
          }
        }
      });

      console.log("[EmojiExplosion] Collected emojis:", loggedEmojis, "from metrics:", Object.keys(valuesToSave));

      // Refresh today's entry and credits so UI updates immediately
      queryClient.invalidateQueries({ queryKey: ["today-entry", userId, entryDate] });
      queryClient.invalidateQueries({ queryKey: ["daily-entry", userId, entryDate] });
      queryClient.invalidateQueries({ queryKey: ["daily-entries"] });
      queryClient.invalidateQueries({ queryKey: ["user-credits", userId] });

      // Force refetch of daily-entries to ensure ContributionChart updates immediately on home page
      await queryClient.refetchQueries({ queryKey: ["daily-entries"] });

      // Force refetch of metric preferences to ensure Track page updates
      await queryClient.refetchQueries({ queryKey: ["metric-preferences", userId] });

      // Trigger emoji explosion via callback (parent will manage the animation)
      if (loggedEmojis.length > 0) {
        console.log("[EmojiExplosion] Triggering explosion with emojis:", loggedEmojis);
        onEmojiExplosion?.(loggedEmojis);
      } else {
        console.log("[EmojiExplosion] No emojis collected, skipping explosion");
      }

      // Trigger tutorial if this is the first successful track
      // Pass the first updated metric info
      const firstMetricKey = Object.keys(valuesToSave)[0];
      if (firstMetricKey && onTutorialTrigger) {
        const metricDef = metricDefinitions[firstMetricKey];
        const isBoolean = booleanKeys.has(firstMetricKey);
        onTutorialTrigger({
          metricKey: firstMetricKey,
          metricName: metricDef?.name || firstMetricKey,
          isBoolean,
        });
      }

      // Animate progress to 100% before closing
      stopProgressAnimation();
      setSaveProgress(100);
      hapticSuccess();

      // Hold at 100% briefly for a satisfying completion feel
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear state BEFORE closing dialog to prevent visual artifacts
      setEditedValues({});
      setRemovedCustomMetricIndices(new Set());
      setEditedSuggestions(new Map());
      setValidationError(null);
      setManuallyAddedMetrics([]);
      setEnabledOptionalIndices(new Set());
      setItemQuantities({});
      setRemovedMetrics(new Set());

      // Close dialog after state is cleared
      onOpenChange(false);

    } catch (error) {
      console.error("Error saving:", error);
      toast.error(t('notifications.toast.failed_to_save_entry'));
    } finally {
      resetProgress();
      setSaving(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRemoveMetric = (key: string) => {
    setRemovedMetrics((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });
  };

  const handleRemoveCustomMetricSuggestion = (index: number) => {
    setRemovedCustomMetricIndices(prev => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
    // Clear validation error when user removes a suggestion
    setValidationError(null);
  };

  const handleEditCustomMetricSuggestion = (index: number) => {
    // Find the suggestion data (check if it's been edited first)
    const edited = editedSuggestions.get(index);
    const suggestion = edited || suggestedCustomMetrics[index];
    if (suggestion) {
      setEditingSuggestion({
        ...suggestion,
        originalIndex: index,
      });
      setCustomMetricsDialogOpen(true);
    }
  };

  // Handlers for AddMetricDialog
  const handleAddBuiltInMetric = (metricKey: string) => {
    // Always remove from removedMetrics first so a previously removed metric can be re-added
    setRemovedMetrics((prev) => {
      if (!prev.has(metricKey)) return prev;
      const newSet = new Set(prev);
      newSet.delete(metricKey);
      return newSet;
    });

    // Check if already in validatedSuggestedValues
    if (validatedSuggestedValues[metricKey] !== undefined) {
      console.log(`[QuickEntryConfirmDialog] Metric ${metricKey} already exists in validatedSuggestedValues`);
      return;
    }

    // Check if already in editedValues
    if (editedValues[metricKey] !== undefined) {
      console.log(`[QuickEntryConfirmDialog] Metric ${metricKey} already exists in editedValues`);
      return;
    }

    // Add the metric to editedValues with initial value
    const metricDef = metricDefinitions[metricKey];
    if (metricDef) {
      // Check if it's a boolean metric
      const isBoolean = metricKey === "ate_healthy" || metricKey === "low_sugars" || metricKey === "fasted";

      // Set initial value in editedValues
      setEditedValues((prev: any) => ({
        ...prev,
        [metricKey]: isBoolean ? "0" : "0",
      }));
    }
  };

  const handleAddExistingCustomMetric = (metric: any) => {
    // Always remove from removedMetrics first so a previously removed metric can be re-added
    const valueKey = `${metric.metric_key}_value`;
    setRemovedMetrics((prev) => {
      const keysToRemove = [valueKey, metric.metric_key, `__manual_existing_${metric.metric_key}`];
      const hasAny = keysToRemove.some(k => prev.has(k));
      if (!hasAny) return prev;
      const newSet = new Set(prev);
      keysToRemove.forEach(k => newSet.delete(k));
      return newSet;
    });

    // Check if this metric has already been manually added
    const metricKey = `__manual_existing_${metric.metric_key}`;
    const alreadyAdded = manuallyAddedMetrics.some(m => m.key === metricKey);

    if (alreadyAdded) {
      console.log(`[QuickEntryConfirmDialog] Metric ${metricKey} already added`);
      return;
    }

    // Check if it's already in the validated values (from AI suggestions)
    if (validatedSuggestedValues[valueKey] !== undefined) {
      console.log(`[QuickEntryConfirmDialog] Metric ${valueKey} already in validatedSuggestedValues`);
      return;
    }

    // Add the existing custom metric to the manually added list
    const isBoolean = (metric as any).metric_type === "boolean";

    // Extract slot number and fetch the actual goal from user_goals
    const slotMatch = metric.metric_key.match(/^custom_(\d+)$/);
    const slotNumber = slotMatch ? slotMatch[1] : null;
    const goalKey = slotNumber ? `custom_${slotNumber}_goal` : null;
    const actualGoal = goalKey && userGoals ? ((userGoals as any)[goalKey] || 100) : 100;

    // Get current value from existingEntry for the progress bar
    const currentValue = existingEntry?.[valueKey] ?? 0;

    console.log(`[QuickEntryConfirmDialog] Adding existing custom metric:`, {
      metricKey: metric.metric_key,
      slotNumber,
      goalKey,
      actualGoal,
      valueKey,
      currentValue,
      userGoalsKeys: userGoals ? Object.keys(userGoals).filter(k => k.includes('custom')) : 'no userGoals'
    });

    setManuallyAddedMetrics(prev => [...prev, {
      key: metricKey,
      name: metric.metric_name,
      emoji: metric.metric_emoji,
      unit: isBoolean ? "" : metric.metric_unit,
      type: isBoolean ? "boolean" : "numeric",
      goal: isBoolean ? 1 : actualGoal,
      value: currentValue, // Store current value for progress bar
      inverse: (metric as any).is_inverse || false,
      existingMetricKey: metric.metric_key, // Store the original metric key
    } as any]);

    // Set initial input value to 0 (user will enter an increment to add)
    setEditedValues((prev: any) => ({
      ...prev,
      [metricKey]: "0",
    }));
  };

  const handleCreateCustomMetric = (newMetric: any) => {
    // Check if a metric with the same characteristics already exists in manuallyAddedMetrics
    const isDuplicate = manuallyAddedMetrics.some(m =>
      m.name === newMetric.name &&
      m.emoji === newMetric.emoji &&
      m.unit === newMetric.unit &&
      m.type === newMetric.type
    );

    if (isDuplicate) {
      console.log('[QuickEntryConfirmDialog] Duplicate custom metric detected, not adding:', newMetric.name);
      toast.error(`A metric with these details is already in your list`);
      return;
    }

    // Add the new custom metric to the manually added list
    setManuallyAddedMetrics(prev => [...prev, newMetric]);

    // Set initial value to 0
    setEditedValues((prev: any) => ({
      ...prev,
      [newMetric.key]: newMetric.type === "boolean" ? "0" : "0",
    }));
  };

  // Handler for saving goal edits
  const handleSaveGoal = async (metricKey: string, newGoalValue: number) => {
    try {
      // Determine the goal key based on metric type
      let goalKey: string;

      if (metricKey.startsWith('custom_') && metricKey.endsWith('_value')) {
        // Custom metric: custom_1_value -> custom_1_goal
        goalKey = metricKey.replace('_value', '_goal');
      } else if (metricKey.startsWith('suggested_custom_')) {
        // Suggested custom metric - update in local state
        const index = parseInt(metricKey.replace('suggested_custom_', ''));
        const edited = editedSuggestions.get(index);
        const suggestion = edited || suggestedCustomMetrics[index];

        if (suggestion) {
          const updatedSuggestion = { ...suggestion, goal: newGoalValue };
          setEditedSuggestions(prev => new Map(prev).set(index, updatedSuggestion));
        }

        setEditingGoalFor(null);
        return;
      } else if (metricKey.startsWith('__manual')) {
        // Manually added metric (either existing or new)
        const metric = manuallyAddedMetrics.find(m => m.key === metricKey);

        if (metric) {
          // Update local state
          const metricIndex = manuallyAddedMetrics.findIndex(m => m.key === metricKey);
          const updatedMetrics = [...manuallyAddedMetrics];
          updatedMetrics[metricIndex] = { ...updatedMetrics[metricIndex], goal: newGoalValue };
          setManuallyAddedMetrics(updatedMetrics);

          // If this is an existing custom metric, also save to database
          if ((metric as any).existingMetricKey) {
            const existingKey = (metric as any).existingMetricKey;
            const slotMatch = existingKey.match(/^custom_(\d+)$/);
            if (slotMatch) {
              const goalKey = `custom_${slotMatch[1]}_goal`;

              console.log(`[QuickEntryConfirmDialog] Saving goal for existing custom metric:`, {
                metricKey,
                existingKey,
                goalKey,
                newGoalValue
              });

              const { error } = await supabase
                .from('user_goals')
                .upsert({
                  user_id: userId,
                  [goalKey]: newGoalValue,
                }, {
                  onConflict: 'user_id'
                });

              if (error) {
                console.error('[QuickEntryConfirmDialog] Error saving goal:', error);
                toast.error(t('components.quick_entry_confirm.toasts.goal_save_error'));
              } else {
                queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });
              }
            }
          }
        }

        setEditingGoalFor(null);
        return;
      } else if (metricKey.match(/^custom_\d+$/)) {
        // Newly created custom metric (custom_1, custom_2, etc. but not custom_1_value)
        // This is a metric created during this session that will be saved when the dialog is confirmed
        // Just update local state - goal will be saved to database when the metric is created
        const metric = manuallyAddedMetrics.find(m => m.key === metricKey);

        if (metric) {
          const metricIndex = manuallyAddedMetrics.findIndex(m => m.key === metricKey);
          const updatedMetrics = [...manuallyAddedMetrics];
          updatedMetrics[metricIndex] = { ...updatedMetrics[metricIndex], goal: newGoalValue };
          setManuallyAddedMetrics(updatedMetrics);

          console.log(`[QuickEntryConfirmDialog] Updated goal for newly created custom metric:`, {
            metricKey,
            newGoalValue,
            metricName: metric.name
          });
        }

        setEditingGoalFor(null);
        return;
      } else {
        // Built-in metric: get goal key from definition
        const metricDef = await getMetric(metricKey);
        goalKey = metricDef?.goalKey;

        if (!goalKey) {
          toast.error(t('components.quick_entry_confirm.toasts.goal_edit_unsupported'));
          setEditingGoalFor(null);
          return;
        }
      }

      // Save to database
      const { error } = await supabase
        .from('user_goals')
        .upsert({
          user_id: userId,
          [goalKey]: newGoalValue,
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('[QuickEntryConfirmDialog] Error saving goal:', error);
        toast.error(t('components.quick_entry_confirm.toasts.goal_save_error'));
      } else {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });
        // Don't show toast while dialog is open - user is still editing
      }
    } catch (error) {
      console.error('[QuickEntryConfirmDialog] Error in handleSaveGoal:', error);
      toast.error(t('components.quick_entry_confirm.toasts.goal_save_error'));
    } finally {
      setEditingGoalFor(null);
    }
  };

  // Helper function to render progress bar for custom metric suggestions
  const renderCustomMetricProgressBar = (suggestion: any, deltaValue: number, currentValue: number = 0) => {
    if (suggestion.type === "boolean") {
      return null; // No progress bar for boolean metrics
    }

    // Check if this suggestion corresponds to an existing custom metric in the database
    // If so, use the latest is_relative value from the database instead of the suggestion
    const existingCustomMetric = customMetrics.find(cm =>
      cm.metric_key === suggestion.key || cm.metric_name === suggestion.name
    );

    const isRelative = existingCustomMetric
      ? (existingCustomMetric.is_relative ?? false)
      : (suggestion.isRelative || suggestion.is_relative || false);

    // Don't show progress bar for relative metrics (no goal)
    if (isRelative) {
      return null;
    }

    const goal = suggestion.goal || 100;
    const newValue = currentValue + deltaValue; // Add delta to current value

    if (!goal || goal === 0) {
      return null;
    }

    // Check if this is an inverse metric (overshoot penalty)
    const isInverse = suggestion.inverse || false;
    const willExceedGoal = newValue > goal;

    // Calculate percentages
    const currentPercent = (currentValue / goal) * 100; // Show current value progress
    let deltaPercent: number;
    let overfillPercent: number;

    if (isInverse && willExceedGoal) {
      // For inverse metrics, if new value exceeds goal, show remaining space to goal in green, rest in red
      const remainingToGoal = Math.max(0, goal - currentValue);
      const greenDelta = Math.min(deltaValue, remainingToGoal);
      const redDelta = deltaValue - greenDelta;

      deltaPercent = Math.min((greenDelta / goal) * 100, 100);
      overfillPercent = (redDelta / goal) * 100;
    } else {
      // For normal metrics
      deltaPercent = Math.min((newValue / goal) * 100, 100);
      overfillPercent = Math.max(0, ((newValue - goal) / goal) * 100);
    }

    // Determine the key to use for goal editing
    // For AI-suggested metrics, use the originalIndex-based key
    // For manually added metrics, use the key property directly
    const goalEditKey = typeof suggestion.originalIndex === 'number'
      ? `suggested_custom_${suggestion.originalIndex}`
      : suggestion.key;

    return (
      <div className="space-y-1 mt-2">
        <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
          {/* Delta (green) */}
          {deltaPercent > 0 && (
            <div
              className="bg-green-700 transition-all duration-700 ease-out"
              style={{ width: `${deltaPercent}%` }}
            />
          )}
          {/* Overfill (red for inverse metrics) */}
          {overfillPercent > 0 && (
            <div
              className="bg-red-500 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(overfillPercent, 100 - deltaPercent)}%` }}
            />
          )}
        </div>
        <div className="text-xs text-muted-foreground flex justify-between items-center">
          <span>0 → {newValue.toFixed(1)} {suggestion.unit}</span>
          <div className="flex items-center gap-1">
            {editingGoalFor === goalEditKey ? (
              <>
                <Input
                  type="number"
                  step="0.1"
                  value={editedGoalValue}
                  onChange={(e) => setEditedGoalValue(e.target.value)}
                  className="h-5 w-16 text-xs px-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveGoal(goalEditKey, parseFloat(editedGoalValue) || goal);
                    } else if (e.key === 'Escape') {
                      setEditingGoalFor(null);
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSaveGoal(goalEditKey, parseFloat(editedGoalValue) || goal)}
                  className="h-5 px-2 text-xs"
                >
                  {t('components.quick_entry_confirm.save_goal')}
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditingGoalFor(goalEditKey);
                    setEditedGoalValue(goal.toString());
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                  aria-label={t('accessibility.labels.edit_goal')}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <span>{t('components.quick_entry_confirm.out_of', { goal })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to format item names for display (no truncation)
  const shortenItemName = (name: string): string => {
    return name?.trim() || '';
  };

  // Helper function to generate breakdown text for a metric
  const generateMetricBreakdown = (metricKey: string, items: any[]): { total: string; breakdown: string; unit: string; itemCount: number; citations: number[]; contributions: Array<{ name: string; value: number; displayValue: string; citation: number | null }> } | null => {
    if (!items || items.length === 0) return null;

    // Metrics that should be set (replaced) rather than added - these are absolute values, not additive
    const replaceKeys = new Set(["mood", "self_appearance_rating", "body_weight", "body_fat_percentage"]);
    let isAbsoluteMetric = replaceKeys.has(metricKey);

    // Check if this is a custom metric and if it's marked as non-additive
    if (metricKey.startsWith('custom_') && metricKey.endsWith('_value')) {
      const customMetricKey = metricKey.replace('_value', '');
      const customMetric = customMetrics.find(cm => cm.metric_key === customMetricKey);
      if (customMetric && (customMetric as any).is_additive === false) {
        isAbsoluteMetric = true;
      }
    }

    // Filter items that have this metric and compute their contribution
    const contributions = items
      .map((item: any, itemIndex: number) => {
        // Use adjusted quantity from state, or fall back to original quantity
        const quantity = itemQuantities[itemIndex] ?? item.quantity ?? 1;

        // Look for the metric in the item's values array (new structure)
        let valuePerUnit = null;
        let citation = null;
        if (Array.isArray(item.values)) {
          const valueEntry = item.values.find((v: any) => {
            const normalizedValueKey = normalizeKey(v.key);
            return normalizedValueKey === metricKey;
          });
          if (valueEntry) {
            valuePerUnit = valueEntry.value_per_item;
            citation = valueEntry.citation;
          }
        }

        // Fallback: check if value exists as a direct property (legacy structure)
        if (valuePerUnit === null && item[metricKey] !== undefined) {
          valuePerUnit = item[metricKey];
        }

        if (valuePerUnit !== null && valuePerUnit !== undefined) {
          // Handle both boolean and numeric values
          const numericValue = typeof valuePerUnit === 'boolean' ? (valuePerUnit ? 1 : 0) : valuePerUnit;
          const total = numericValue * quantity;

          if (total > 0) {
            return {
              name: shortenItemName(item.name),
              value: total,
              displayValue: total % 1 === 0 ? total.toString() : total.toFixed(1),
              citation: citation,
            };
          }
        }
        return null;
      })
      .filter(Boolean);

    if (contributions.length === 0) return null;

    // For absolute metrics with only 1 item, don't show breakdown (it's just a SET operation, not ADD)
    if (isAbsoluteMetric && contributions.length === 1) {
      return null;
    }

    // Get the unit for this metric from already-loaded metricDefinitions
    const metricDef = metricDefinitions && typeof metricDefinitions === 'object' && metricDefinitions[metricKey]
      ? metricDefinitions[metricKey] as { unit?: string }
      : undefined;
    const unit = metricDef?.unit || '';

    // Calculate total
    const sum = contributions.reduce((acc: number, c: any) => acc + c.value, 0);
    const sumDisplay = sum % 1 === 0 ? sum.toString() : sum.toFixed(1);

    // Collect all unique citation numbers (filter out null/undefined)
    const citationSet = new Set<number>();
    contributions.forEach((c: any) => {
      if (c.citation !== null && c.citation !== undefined) {
        citationSet.add(c.citation);
      }
    });
    const citations = Array.from(citationSet).sort((a, b) => a - b);

    // Format the breakdown with inline citations
    let breakdownText: string;
    if (contributions.length === 1) {
      const c = contributions[0] as any;
      const citationText = c.citation ? `[${c.citation}]` : '';
      breakdownText = `+${c.displayValue}${citationText}(${c.name})`;
    } else {
      const parts = contributions.map((c: any) => {
        const citationText = c.citation ? `[${c.citation}]` : '';
        return `${c.displayValue}${citationText}(${c.name})`;
      }).join(' + ');
      breakdownText = `${parts} = +${sumDisplay}`;
    }

    return {
      total: `+${sumDisplay}`,
      breakdown: breakdownText,
      unit,
      itemCount: contributions.length,
      citations,
      contributions: contributions as Array<{ name: string; value: number; displayValue: string; citation: number | null }>
    };
  };

  // Helper function to render progress bar for existing custom metrics
  const renderExistingCustomMetricProgressBar = (metricKey: string, deltaValue: number, metricInfo: any) => {
    // Don't show progress bar for boolean metrics (unit is empty string for boolean)
    if (!metricInfo.unit || metricInfo.unit === 'Yes/No') {
      return null;
    }

    // For relative metrics (rating metrics), show value transition without progress bar/goal
    if (metricInfo.isRelative) {
      if (!existingEntry) return null;

      const currentValue = Number((existingEntry as any)?.[metricKey] || 0);
      const newValue = currentValue + deltaValue;

      return (
        <div className="text-xs text-muted-foreground mt-2">
          <span>{currentValue.toFixed(1)} → {newValue.toFixed(1)} {metricInfo.unit}</span>
        </div>
      );
    }

    if (!userGoals || !existingEntry) {
      return null;
    }

    // Extract the goal key (e.g., custom_1_value -> custom_1_goal)
    const goalKey = metricKey.replace('_value', '_goal');
    const goal = (userGoals as any)[goalKey];

    if (!goal || goal === 0) {
      return null;
    }

    // Get the corresponding custom metric to check if it's inverse
    const metricKeyWithoutValue = metricKey.replace('_value', '');
    const customMetric = customMetrics.find(cm => cm.metric_key === metricKeyWithoutValue);
    const isInverse = customMetric ? (customMetric as any)?.is_inverse : false;

    const currentValue = Number((existingEntry as any)?.[metricKey] || 0);
    const newValue = currentValue + deltaValue;
    const willExceedGoal = newValue > goal;

    // Calculate percentages for the three sections
    const currentPercent = Math.min((currentValue / goal) * 100, 100);

    let deltaPercent: number;
    let overfillPercent: number;

    if (isInverse && willExceedGoal) {
      // For inverse metrics, if new value exceeds goal, show remaining space to goal in green, rest in red
      const remainingToGoal = Math.max(0, goal - currentValue);
      const greenDelta = Math.min(deltaValue, remainingToGoal);
      const redDelta = deltaValue - greenDelta;

      deltaPercent = Math.min((greenDelta / goal) * 100, 100 - currentPercent);
      overfillPercent = (redDelta / goal) * 100;
    } else {
      // For normal metrics
      deltaPercent = Math.min(((newValue - currentValue) / goal) * 100, 100 - currentPercent);
      overfillPercent = Math.max(0, ((newValue - goal) / goal) * 100);
    }

    return (
      <div className="space-y-1 mt-2">
        <div className="h-2.5 bg-gray-200 dark:bg-gray-700 system:bg-gray-700 rounded-full overflow-hidden flex">
          {/* Current value (green) */}
          {currentPercent > 0 && (
            <div
              className="bg-green-500 transition-all duration-700 ease-out"
              style={{ width: `${currentPercent}%` }}
            />
          )}
          {/* Delta (green for normal metrics, or green up to goal for inverse) */}
          {deltaPercent > 0 && (
            <div
              className={isInverse && willExceedGoal ? "bg-green-500 transition-all duration-700 ease-out" : "bg-green-700 transition-all duration-700 ease-out"}
              style={{ width: `${deltaPercent}%` }}
            />
          )}
          {/* Overfill (red for inverse metrics) */}
          {overfillPercent > 0 && (
            <div
              className="bg-red-500 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(overfillPercent, 100 - currentPercent - deltaPercent)}%` }}
            />
          )}
        </div>
        <div className="text-xs text-muted-foreground flex justify-between items-center">
          <span>{currentValue.toFixed(1)} → {newValue.toFixed(1)} {metricInfo.unit}</span>
          <div className="flex items-center gap-1">
            {editingGoalFor === metricKey ? (
              <>
                <Input
                  type="number"
                  step="0.1"
                  value={editedGoalValue}
                  onChange={(e) => setEditedGoalValue(e.target.value)}
                  className="h-5 w-16 text-xs px-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveGoal(metricKey, parseFloat(editedGoalValue) || goal);
                    } else if (e.key === 'Escape') {
                      setEditingGoalFor(null);
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSaveGoal(metricKey, parseFloat(editedGoalValue) || goal)}
                  className="h-5 px-2 text-xs"
                >
                  {t('components.quick_entry_confirm.save_goal')}
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditingGoalFor(metricKey);
                    setEditedGoalValue(goal.toString());
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                  aria-label={t('accessibility.labels.edit_goal')}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <span>{t('components.quick_entry_confirm.out_of', { goal })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render progress bar for a metric
  const renderProgressBar = (metricKey: string, deltaValue: number) => {
    // CRITICAL: Verify we have metric definitions before proceeding
    if (!metricDefinitions || Object.keys(metricDefinitions).length === 0) {
      console.error(`[ProgressBar] ${metricKey}: Cannot render - metricDefinitions is empty!`);
      return null;
    }

    const metricDef = metricDefinitions && typeof metricDefinitions === 'object' && metricDefinitions[metricKey]
      ? metricDefinitions[metricKey] as { goalKey?: string; unit?: string; isInverse?: boolean }
      : undefined;

    if (!metricDef) {
      console.error(`[ProgressBar] ${metricKey}: Metric definition not found in metricDefinitions`);
      return null;
    }

    const goalKey = metricDef?.goalKey;
    const unit = metricDef?.unit || '';
    const isInverse = metricDef?.isInverse || false;

    // Debug logging
    console.log(`[ProgressBar] ${metricKey}:`, {
      hasGoalKey: !!goalKey,
      hasUserGoals: !!userGoals,
      hasExistingEntry: !!existingEntry,
      goalKey,
      deltaValue
    });

    if (!goalKey) {
      console.log(`[ProgressBar] ${metricKey}: No goalKey defined in metric definition`);
      return null; // No progress bar for metrics without goals (like mood, appearance)
    }

    if (!userGoals) {
      console.log(`[ProgressBar] ${metricKey}: User goals not loaded yet`);
      return null;
    }

    const goal = (userGoals as any)[goalKey];
    const currentValue = Number((existingEntry as any)?.[metricKey] || 0);
    const newValue = currentValue + deltaValue;

    console.log(`[ProgressBar] ${metricKey}:`, {
      goal,
      currentValue,
      deltaValue,
      newValue
    });

    if (!goal || goal === 0) {
      console.log(`[ProgressBar] ${metricKey}: No goal set or goal is 0`);
      return null;
    }

    // Special handling for inverse metrics (calories, added_sugars) - going over is bad

    // Calculate percentages for the three sections
    const currentPercent = Math.min((currentValue / goal) * 100, 100);
    const willExceedGoal = newValue > goal;

    let deltaPercent: number;
    let overfillPercent: number;

    if (isInverse && willExceedGoal) {
      // For inverse metrics, if new value exceeds goal, show remaining space to goal in green, rest in red
      const remainingToGoal = Math.max(0, goal - currentValue);
      const greenDelta = Math.min(deltaValue, remainingToGoal);
      const redDelta = deltaValue - greenDelta;

      deltaPercent = Math.min((greenDelta / goal) * 100, 100 - currentPercent);
      overfillPercent = (redDelta / goal) * 100;
    } else {
      // For other metrics, normal behavior
      deltaPercent = Math.min(((newValue - currentValue) / goal) * 100, 100 - currentPercent);
      overfillPercent = Math.max(0, ((newValue - goal) / goal) * 100);
    }

    return (
      <div className="space-y-1 mt-2">
        <div className="h-2.5 bg-gray-200 dark:bg-gray-700 system:bg-gray-700 rounded-full overflow-hidden flex">
          {/* Current value (green) */}
          {currentPercent > 0 && (
            <div
              className="bg-green-500 transition-all duration-700 ease-out"
              style={{ width: `${currentPercent}%` }}
            />
          )}
          {/* Delta (green for normal metrics, or green up to goal for inverse metrics) */}
          {deltaPercent > 0 && (
            <div
              className={isInverse && willExceedGoal ? "bg-green-500 transition-all duration-700 ease-out" : "bg-green-700 transition-all duration-700 ease-out"}
              style={{ width: `${deltaPercent}%` }}
            />
          )}
          {/* Overfill (red) */}
          {overfillPercent > 0 && (
            <div
              className="bg-red-500 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(overfillPercent, 100 - currentPercent - deltaPercent)}%` }}
            />
          )}
        </div>
        <div className="text-xs text-muted-foreground flex justify-between items-center">
          <span>{currentValue.toFixed(1)} → {newValue.toFixed(1)} {unit}</span>
          <div className="flex items-center gap-1">
            {editingGoalFor === metricKey ? (
              <>
                <Input
                  type="number"
                  step="0.1"
                  value={editedGoalValue}
                  onChange={(e) => setEditedGoalValue(e.target.value)}
                  className="h-5 w-16 text-xs px-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveGoal(metricKey, parseFloat(editedGoalValue) || goal);
                    } else if (e.key === 'Escape') {
                      setEditingGoalFor(null);
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSaveGoal(metricKey, parseFloat(editedGoalValue) || goal)}
                  className="h-5 px-2 text-xs"
                >
                  {t('components.quick_entry_confirm.save_goal')}
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditingGoalFor(metricKey);
                    setEditedGoalValue(goal.toString());
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                  aria-label={t('accessibility.labels.edit_goal')}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <span>{t('components.quick_entry_confirm.out_of', { goal })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Early returns before any processing
  if (!suggestedValues) return null;

  // Wait for metric definitions to load (handles error case only)
  if (isLoadingMetricDefinitions && !metricDefinitions) {
    return null; // Let parent component handle loading state
  }

  if (metricDefinitionsError) {
    console.error("[QuickEntryConfirmDialog] Error loading metric definitions:", metricDefinitionsError);
    return null; // Let parent component handle error state
  }

  // Extract suggested metric keys for later validation
  const suggestedMetricKeys = Object.keys(suggestedValues).filter(k =>
    k !== 'aiComment' && k !== 'comment' && k !== 'healthierReason' &&
    k !== 'healthierAlternative' && k !== 'suggestedCustomMetrics' && k !== 'items'
  );

  // Extract AI comment, healthier reason, items, and references
  const aiComment = suggestedValues.aiComment;
  const healthierReason = suggestedValues.healthierReason;
  const items = suggestedValues.items || [];
  const references = suggestedValues.references || null;

  console.log("[QuickEntryConfirmDialog] References data:", {
    hasReferences: !!references,
    referencesType: Array.isArray(references) ? 'array' : typeof references,
    referencesLength: Array.isArray(references) ? references.length : 0,
    rawReferences: references
  });

  // Compute normalized suggested values with adjusted item quantities
  // This recalculates whenever itemQuantities changes
  const normalizedSuggestedValues: Record<string, any> = (() => {
    const normalized: Record<string, any> = Object.fromEntries(
      Object.entries(suggestedValues).map(([k, v]) => [normalizeKey(k), v])
    );

    // Extract metric values from items array and add to normalizedSuggestedValues
    // This handles cases where AI only returns metrics in the items array
    // Use adjusted quantities from itemQuantities state
    if (Array.isArray(items) && items.length > 0) {
      items.forEach((item: any, itemIndex: number) => {
        if (Array.isArray(item.values)) {
          item.values.forEach((valueEntry: any) => {
            if (valueEntry.key && valueEntry.value_per_item !== undefined) {
              const normalizedKey = normalizeKey(valueEntry.key);

              // Skip if this metric was already provided in top-level suggestedValues
              // to avoid double-counting
              if (normalized[normalizedKey] !== undefined && normalizedKey !== 'items') {
                console.log(`[QuickEntryConfirmDialog] Skipping item extraction for ${valueEntry.key} -> ${normalizedKey} (already in suggestedValues)`)
                return;
              }

              // Use adjusted quantity from state, or fall back to original quantity
              const quantity = itemQuantities[itemIndex] ?? item.quantity ?? 1;
              const valuePerUnit = typeof valueEntry.value_per_item === 'boolean'
                ? (valueEntry.value_per_item ? 1 : 0)
                : valueEntry.value_per_item;
              const totalValue = valuePerUnit * quantity;

              normalized[normalizedKey] = totalValue;
              console.log(`[QuickEntryConfirmDialog] Extracted from items: ${valueEntry.key} -> ${normalizedKey} = ${totalValue} (adjusted qty: ${quantity})`);
            }
          });
        }
      });
    }

    return normalized;
  })();

  // Extract suggested custom metrics
  const suggestedCustomMetrics = (suggestedValues.suggestedCustomMetrics || normalizedSuggestedValues.suggestedCustomMetrics || []) as Array<{
    name: string;
    type: "numeric" | "boolean";
    unit: string;
    emoji: string;
    goal: number | null;
    value: number;
    inverse: boolean;
    is_additive?: boolean;
    is_optional?: boolean;
    is_relative?: boolean;
  }>;

  // Check if this is a nutrition-related request by checking if any items are food
  const isNutritionRelated = items && Array.isArray(items) && items.length > 0 && items.some((item: any) => item.is_food === true);

  // Debug logging
  console.log("[QuickEntryConfirmDialog] Extracted values:", {
    aiComment,
    healthierReason,
    isNutritionRelated,
    suggestedValues: Object.keys(suggestedValues),
    normalized: Object.keys(normalizedSuggestedValues),
    rawHealthierReason: suggestedValues.healthierReason,
  });

  // Create a Set of all valid metric keys (built-in + custom)
  const validMetricKeys = new Set<string>([
    ...(metricDefinitions && typeof metricDefinitions === 'object' ? Object.keys(metricDefinitions) : []),
    ...customMetrics.map(cm => {
      // Extract slot number from metric_key (e.g., "custom_6" -> "6")
      const slotMatch = cm.metric_key.match(/^custom_(\d+)$/);
      if (slotMatch) {
        return `custom_${slotMatch[1]}_value`;
      }
      // If already in _value format, return as-is
      if (cm.metric_key.match(/^custom_(\d+)_value$/)) {
        return cm.metric_key;
      }
      // LEGACY: Handle bad data where metric_key is descriptive (e.g., "custom_piano_lessons")
      // This shouldn't happen with current code, but handle gracefully
      console.warn(`[QuickEntryConfirmDialog] Invalid custom metric key format: ${cm.metric_key} (${cm.metric_name}). Please run fix-custom-metric-keys.sql to repair database.`);
      return null; // Will be filtered out
    }).filter(Boolean) as string[]
  ]);

  console.log('[QuickEntryConfirmDialog] Valid metric keys:', Array.from(validMetricKeys).filter(k => k.startsWith('custom_')));
  console.log('[QuickEntryConfirmDialog] Normalized suggested values:', normalizedSuggestedValues);

  // Filter out any invalid metric keys before rendering
  const validatedSuggestedValues: Record<string, any> = {};
  for (const [key, value] of Object.entries(normalizedSuggestedValues)) {
    // Skip special keys that aren't metrics
    if (key === 'aiComment' || key === 'healthierAlternative' || key === 'healthierReason' ||
      key === 'suggestedCustomMetrics' || key === 'items') continue;

    if (validMetricKeys.has(key)) {
      validatedSuggestedValues[key] = value;
    } else {
      console.warn(`[QuickEntryConfirmDialog] Filtered out invalid metric key: ${key}`);
    }
  }

  // Show all suggested metrics (even if not currently enabled), excluding removed ones
  // Also include built-in metrics that were manually added (in editedValues but not in validatedSuggestedValues)
  const allMetricKeys = new Set([
    ...Object.keys(validatedSuggestedValues),
    ...Object.keys(editedValues).filter(k => {
      // Only include built-in metrics (not custom or manual keys)
      return !k.startsWith('custom_') &&
        !k.startsWith('__manual') &&
        !k.startsWith('__custom_suggestion') &&
        validMetricKeys.has(k);
    })
  ]);

  const metricsWithValues = Array.from(allMetricKeys)
    .filter((key) => {
      if (removedMetrics.has(key)) return false;

      // Check if value exists in either validatedSuggestedValues or editedValues
      const hasValue = (validatedSuggestedValues[key] !== null && validatedSuggestedValues[key] !== undefined) ||
        (editedValues[key] !== null && editedValues[key] !== undefined);
      if (!hasValue) return false;

      // If metric was manually added via editedValues (not from AI suggestions), always show it
      const wasManuallyAdded = editedValues[key] !== undefined && validatedSuggestedValues[key] === undefined;

      // Filter based on toggle state - but always show manually added metrics
      if (!wasManuallyAdded && !showDisabledMetrics && !isMetricEnabled(key)) {
        return false;
      }

      return true;
    })
    .map((key) => {
      // Check if it's a custom metric (format: custom_1_value, custom_2_value, etc.)
      if (key.startsWith('custom_') && key.endsWith('_value')) {
        // Extract the metric key (e.g., "custom_1" from "custom_1_value")
        const metricKey = key.replace('_value', '');
        // Find the custom metric by metric_key instead of array index
        const customMetric = customMetrics.find(cm => cm.metric_key === metricKey);
        if (customMetric) {
          const isBoolean = (customMetric as any).metric_type === "boolean";
          return {
            key,
            name: customMetric.metric_name,
            unit: isBoolean ? t('components.custom_metrics_dialog.type_yes_no') : customMetric.metric_unit,
            emoji: customMetric.metric_emoji,
            isEnabled: true,
            isBoolean,
            isInverse: (customMetric as any).is_inverse || false,
            isRelative: (customMetric as any).is_relative || false,
          };
        }
      }

      // Otherwise, it's a built-in metric
      const def = (metricDefinitions && typeof metricDefinitions === 'object' && metricDefinitions[key])
        ? metricDefinitions[key] as { name?: string; label?: string; unit?: string; emoji?: string }
        : undefined;

      // CRITICAL: Get the display name using centralized localization function
      const displayName = getLocalizedMetricName(key, t, def?.name || def?.label || key);

      // CRITICAL: If we don't have ANY display name, DO NOT render
      if (!def || !displayName) {
        console.error(`[QuickEntryConfirmDialog] CRITICAL: Missing definition or name for built-in metric: ${key}`, {
          metricDefinitionsExists: !!metricDefinitions,
          metricDefinitionsKeys: metricDefinitions ? Object.keys(metricDefinitions).length : 0,
          sampleKeys: metricDefinitions ? Object.keys(metricDefinitions).slice(0, 3) : [],
          allKeys: Object.keys(metricDefinitions),
          def: def,
          defHasName: def ? !!def.name : false,
          defHasLabel: def ? !!def.label : false,
          defKeys: def ? Object.keys(def) : []
        });
        // Return null to filter this out rather than showing the key
        return null;
      }

      const isBuiltInBoolean = key === "ate_healthy" || key === "low_sugars" || key === "fasted";
      return {
        key,
        name: displayName, // Use localized name
        unit: def.unit || "",
        emoji: def.emoji || "",
        isEnabled: isMetricEnabled(key),
        isBoolean: isBuiltInBoolean,
      };
    })
    .filter(Boolean); // Remove any null entries

  // Collect names of custom metrics already resolved into metricsWithValues
  // (e.g. after DB insert + query refetch during save, the metric appears in customMetrics prop)
  const resolvedCustomMetricNames = new Set(
    metricsWithValues
      .filter((m: any) => m.key?.startsWith('custom_') && m.key?.endsWith('_value'))
      .map((m: any) => (m.name || '').toLowerCase().trim())
      .filter(Boolean)
  );

  // Add suggested custom metrics to the list (if not removed and not already resolved)
  const customMetricsToCreate = suggestedCustomMetrics
    .map((suggestion, idx) => {
      // Check if this suggestion has been edited
      const edited = editedSuggestions.get(idx);
      return {
        ...(edited || suggestion),
        originalIndex: idx,
      };
    })
    .filter(suggestion => !removedCustomMetricIndices.has(suggestion.originalIndex))
    // Skip suggestions already resolved into real custom metrics (avoids duplication
    // when query refetch completes while the dialog is still open during save)
    .filter(suggestion => !resolvedCustomMetricNames.has((suggestion.name || '').toLowerCase().trim()))
    .map(suggestion => ({
      key: `__custom_suggestion_${suggestion.originalIndex}`, // Temporary key
      name: suggestion.name,
      unit: suggestion.type === "boolean" ? t('components.custom_metrics_dialog.type_yes_no') : suggestion.unit,
      emoji: suggestion.emoji,
      isEnabled: true,
      isCustomSuggestion: true,
      customSuggestionData: suggestion,
    }));

  // Add manually added metrics to the display (skip any already resolved into real custom metrics)
  const manualMetricsToDisplay = manuallyAddedMetrics
    .filter((metric) => !resolvedCustomMetricNames.has((metric.name || '').toLowerCase().trim()) || !!(metric as any).existingMetricKey)
    .map((metric) => ({
    key: metric.key,
    name: metric.name,
    unit: metric.type === "boolean" ? t('components.custom_metrics_dialog.type_yes_no') : metric.unit,
    emoji: metric.emoji,
    isEnabled: true,
    isCustomSuggestion: true,
    isManuallyAdded: true,
    isExistingMetric: !!(metric as any).existingMetricKey, // Mark if this is an existing metric
    customSuggestionData: metric,
  }));

  const allMetricsToDisplay = [...metricsWithValues, ...customMetricsToCreate, ...manualMetricsToDisplay];

  // Check if all displayed metrics are NEW custom metrics (not existing ones)
  // New custom metrics are those with isCustomSuggestion: true AND NOT isExistingMetric: true
  const allMetricsAreNewCustom = allMetricsToDisplay.length > 0 &&
    allMetricsToDisplay.every((metric: any) => {
      return metric.isCustomSuggestion && !metric.isExistingMetric;
    });

  // Check if all displayed metrics are boolean
  const allMetricsAreBoolean = allMetricsToDisplay.length > 0 &&
    allMetricsToDisplay.every((metric: any) => {
      // For custom suggestions, check the type from customSuggestionData
      if (metric.isCustomSuggestion) {
        return metric.customSuggestionData?.type === "boolean";
      }
      return metric.isBoolean;
    });

  console.log("[QuickEntryConfirmDialog] Healthier alternative display check:", {
    isNutritionRelated,
    allMetricsAreBoolean,
    hasHealthierReason: !!healthierReason,
    willShow: !allMetricsAreBoolean && !!healthierReason,
    metricsCount: allMetricsToDisplay.length,
  });

  // Count disabled metrics for toggle label
  const normalizedSuggestedKeys = Object.keys(validatedSuggestedValues)
    .filter(k => k !== 'comment' && !removedMetrics.has(k));

  // Get manually added built-in metric keys (those in editedValues but not in validatedSuggestedValues)
  const manuallyAddedBuiltInKeys = Object.keys(editedValues).filter(k =>
    !k.startsWith('custom_') &&
    !k.startsWith('__manual') &&
    !k.startsWith('__custom_suggestion') &&
    validatedSuggestedValues[k] === undefined
  );

  // Exclude manually added metrics from the disabled count
  const disabledMetricsCount = normalizedSuggestedKeys
    .filter(key => !isMetricEnabled(key) && !manuallyAddedBuiltInKeys.includes(key))
    .length;
  const enabledMetricsCount = normalizedSuggestedKeys.filter(key => isMetricEnabled(key)).length;

  // Check if user has ANY metrics enabled at all (across all categories)
  const hasAnyMetricsEnabled = (
    (metricPreferences?.nutrition_metrics?.length || 0) > 0 ||
    (metricPreferences?.activity_sleep_metrics?.length || 0) > 0 ||
    (metricPreferences?.results_metrics?.length || 0) > 0
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        type: "spring" as const,
        stiffness: 300,
        damping: 24
      }
    })
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md lg:max-w-lg flex flex-col max-h-[85vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t('components.quick_entry_confirm.title', {
                date: (() => {
                  const today = new Date();
                  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                  return isToday ? t('track.today') : format(selectedDate, 'MMM do');
                })()
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-0 pb-4 px-2 sm:px-1 overflow-y-auto flex-1 min-h-0">
            {/* AI Comment - subtle note at the top */}
            {aiComment && (
              <div className="text-[11px] text-muted-foreground/70 italic px-2 py-1.5">
                <span>{aiComment}</span>
              </div>
            )}

            {/* Items Breakdown - Collapsible section to adjust quantities */}
            {items && Array.isArray(items) && items.length > 0 && isNutritionRelated && (
              <Collapsible open={itemsBreakdownExpanded} onOpenChange={setItemsBreakdownExpanded}>
                <CollapsibleTrigger className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span>{t('components.quick_entry_confirm.adjust_counts')}</span>
                    <span className="text-xs text-muted-foreground">
                      {items.length === 1
                        ? t('components.quick_entry_confirm.items_count', { count: items.length })
                        : t('components.quick_entry_confirm.items_count_plural', { count: items.length })}
                    </span>
                  </div>
                  {itemsBreakdownExpanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 space-y-2 px-2">
                    {items.map((item: any, itemIndex: number) => (
                      <div key={itemIndex} className="flex flex-col gap-2 py-2 px-3 rounded-lg bg-muted/30 border border-muted">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium break-words">{item.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {item.quantity || 1} {item.quantity === 1 ? t('components.quick_entry_confirm.serving') : t('components.quick_entry_confirm.serving_plural')}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const currentQty = itemQuantities[itemIndex] ?? item.quantity ?? 1;
                              if (currentQty > 0.25) {
                                setItemQuantities(prev => ({
                                  ...prev,
                                  [itemIndex]: Math.round(Math.max(0.25, currentQty - 0.25) * 100) / 100
                                }));
                              }
                            }}
                            className="h-7 w-7 p-0"
                          >
                            -
                          </Button>
                          <Input
                            type="number"
                            step="0.25"
                            min="0.25"
                            value={itemQuantities[itemIndex] ?? item.quantity ?? 1}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val >= 0.25) {
                                setItemQuantities(prev => ({
                                  ...prev,
                                  [itemIndex]: val
                                }));
                              }
                            }}
                            className="h-7 w-16 text-center text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const currentQty = itemQuantities[itemIndex] ?? item.quantity ?? 1;
                              setItemQuantities(prev => ({
                                ...prev,
                                [itemIndex]: Math.round((currentQty + 0.25) * 100) / 100
                              }));
                            }}
                            className="h-7 w-7 p-0"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {allMetricsToDisplay.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <p className="text-sm text-muted-foreground text-center">
                  {t('components.quick_entry_confirm.no_metrics_message')}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAddMetricDialogOpen(true)}
                  className="h-7 px-2 text-[10px] border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 bg-background"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {t('components.quick_entry_confirm.add_metric_button')}
                </Button>
              </div>
            ) : (
              <motion.div
                className="space-y-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence mode="popLayout">
                  {allMetricsToDisplay.map((metric: any, index: number) => {
                    // Check if this is a custom metric suggestion
                    if (metric.isCustomSuggestion) {
                      const suggestion = metric.customSuggestionData;
                      const suggestionKey = metric.key;
                      const isManuallyAdded = metric.isManuallyAdded === true;
                      const isOptional = !isManuallyAdded && suggestion.is_optional === true;
                      const isEnabled = !isManuallyAdded && enabledOptionalIndices.has(suggestion.originalIndex);

                      // Get the delta value (increment) and current value for progress bar
                      const deltaValue = Number(
                        editedValues[suggestionKey] !== undefined
                          ? editedValues[suggestionKey]
                          : suggestion.value
                      ) || 0;

                      // For manually added existing metrics, use stored current value; otherwise 0
                      const currentValueForProgressBar = isManuallyAdded && metric.isExistingMetric
                        ? (suggestion.value || 0)
                        : 0;

                      // If optional and not enabled, render as disabled with subtle Add button
                      if (isOptional && !isEnabled) {
                        return (
                          <motion.div key={suggestionKey} custom={index} variants={itemVariants} layout className="space-y-2 opacity-50">
                            <div className="flex items-center justify-between">
                              <div className="text-[11px] flex items-center gap-1.5 text-muted-foreground">
                                {metric.emoji && <span className="text-xs">{metric.emoji}</span>}
                                <span>{metric.name}</span>
                                <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
                                  {t('components.quick_entry_confirm.suggestion_badge')}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEnabledOptionalIndices(prev => new Set(prev).add(suggestion.originalIndex));
                                }}
                                className="h-5 px-2 text-[10px] border-muted-foreground/20 hover:border-muted-foreground/40"
                              >
                                {t('components.quick_entry_confirm.add_button')}
                              </Button>
                            </div>
                          </motion.div>
                        );
                      }

                      return (
                        <motion.div key={suggestionKey} custom={index} variants={itemVariants} layout className="space-y-2">
                          <div className="flex items-center justify-between">
                            {isManuallyAdded ? (
                              <div className={`${metric.name.length > 20 ? 'text-xs' : 'text-sm'} flex items-center gap-2`}>
                                {metric.emoji && <span>{metric.emoji}</span>}
                                <span>{metric.name}</span>
                                {!metric.isExistingMetric && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                    new
                                  </span>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleEditCustomMetricSuggestion(suggestion.originalIndex)}
                                className={`${metric.name.length > 20 ? 'text-xs' : 'text-sm'} flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity`}
                              >
                                {metric.emoji && <span>{metric.emoji}</span>}
                                <span>{metric.name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors flex items-center gap-1">
                                  {t('components.quick_entry_confirm.new_badge')}
                                  <Pencil className="w-3 h-3 text-muted-foreground" />
                                </span>
                              </button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (isManuallyAdded) {
                                  // Remove from manually added metrics
                                  setManuallyAddedMetrics(prev => prev.filter(m => m.key !== suggestionKey));
                                  // Remove from edited values
                                  setEditedValues(prev => {
                                    const newValues = { ...prev };
                                    delete newValues[suggestionKey];
                                    return newValues;
                                  });
                                } else {
                                  handleRemoveCustomMetricSuggestion(suggestion.originalIndex);
                                }
                              }}
                              className="h-6 px-2 text-xs"
                            >
                              {t('components.quick_entry_confirm.remove_button')}
                            </Button>
                          </div>
                          {suggestion.type === "boolean" ? (
                            <div className="flex items-center space-x-2 py-2">
                              <Checkbox
                                id={suggestionKey}
                                checked={
                                  editedValues[suggestionKey] !== undefined
                                    ? Number(editedValues[suggestionKey]) > 0
                                    : Boolean(suggestion.value)
                                }
                                onCheckedChange={(checked) => handleValueChange(suggestionKey, checked ? "1" : "0")}
                              />
                              <Label htmlFor={suggestionKey} className="text-sm font-normal cursor-pointer">
                                {suggestion.inverse ? t('components.quick_entry_confirm.yes_mark_done') : t('components.quick_entry_confirm.yes')}
                              </Label>
                            </div>
                          ) : (
                            <Input
                              id={suggestionKey}
                              type="number"
                              step="0.1"
                              value={
                                editedValues[suggestionKey] !== undefined
                                  ? editedValues[suggestionKey]
                                  : suggestion.value
                              }
                              onChange={(e) => handleValueChange(suggestionKey, e.target.value)}
                              className="w-full max-w-full"
                            />
                          )}
                          {renderCustomMetricProgressBar(suggestion, deltaValue, currentValueForProgressBar)}
                        </motion.div>
                      );
                    }

                    // Regular metric handling
                    const metricDef = metricDefinitions[metric.key] as { preventQuickEntryDelete?: boolean } | undefined;
                    const canDelete = !metricDef?.preventQuickEntryDelete;

                    // CRITICAL SAFETY CHECK: Verify metric has a valid display name
                    // If the name matches the key, we're showing a raw key - this should NEVER happen
                    if (metric.name === metric.key || !metric.name) {
                      console.error(`[QuickEntryConfirmDialog] CRITICAL: About to render metric key as name!`, {
                        metricKey: metric.key,
                        metricName: metric.name,
                        hasMetricDef: !!metricDef,
                        metricDefKeys: metricDef ? Object.keys(metricDef) : [],
                      });
                      // Skip rendering this metric entirely
                      return null;
                    }

                    // Get the delta value for progress bar
                    const deltaValue = Number(
                      editedValues[metric.key] !== undefined
                        ? editedValues[metric.key]
                        : validatedSuggestedValues[metric.key]
                    ) || 0;

                    // Check if this is an existing custom metric (custom_X_value pattern)
                    const isExistingCustomMetric = metric.key.startsWith('custom_') && metric.key.endsWith('_value');
                    const isBoolean = metric.isBoolean || false;

                    return (
                      <motion.div key={metric.key} custom={index} variants={itemVariants} layout className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={metric.key} className={`${metric.name.length > 20 ? 'text-xs' : 'text-sm'} flex items-center gap-2`}>
                            {metric.emoji && <span>{metric.emoji}</span>}
                            <span>{metric.name}</span>
                          </Label>
                          {canDelete ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMetric(metric.key)}
                              className="h-6 px-2 text-xs"
                            >
                              {t('components.quick_entry_confirm.remove_button')}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('components.quick_entry_confirm.auto_synced')}</span>
                          )}
                        </div>
                        {isBoolean ? (
                          <div className="flex items-center space-x-2 py-2">
                            <Checkbox
                              id={metric.key}
                              checked={
                                editedValues[metric.key] !== undefined
                                  ? Number(editedValues[metric.key]) > 0
                                  : Boolean(validatedSuggestedValues[metric.key])
                              }
                              onCheckedChange={(checked) => handleValueChange(metric.key, checked ? "1" : "0")}
                            />
                            <Label htmlFor={metric.key} className="text-sm font-normal cursor-pointer">
                              {metric.isInverse ? t('components.quick_entry_confirm.yes_mark_done') : t('components.quick_entry_confirm.yes')}
                            </Label>
                          </div>
                        ) : (
                          <Input
                            id={metric.key}
                            type="number"
                            step="0.1"
                            value={
                              editedValues[metric.key] !== undefined
                                ? editedValues[metric.key]
                                : validatedSuggestedValues[metric.key]
                            }
                            onChange={(e) => handleValueChange(metric.key, e.target.value)}
                            className="w-full max-w-full"
                            disabled={!canDelete}
                          />
                        )}
                        {!isBoolean && isExistingCustomMetric
                          ? renderExistingCustomMetricProgressBar(metric.key, deltaValue, metric)
                          : !isBoolean && renderProgressBar(metric.key, deltaValue)
                        }
                        {/* Display breakdown for nutrition metrics when items are available */}
                        {!isBoolean && items && items.length > 0 && (
                          (() => {
                            const breakdownData = generateMetricBreakdown(metric.key, items);
                            if (!breakdownData) return null;

                            const isExpanded = expandedBreakdowns.has(metric.key);
                            const hasMultipleItems = breakdownData.itemCount > 1;

                            // Helper to get reference info for a citation number
                            const getReference = (citationNum: number | null) => {
                              if (!citationNum || !references || !Array.isArray(references)) return null;
                              return references[citationNum - 1]; // Convert 1-based to 0-based index
                            };

                            return (
                              <div className="text-[10px] text-muted-foreground/60 mt-1">
                                {isExpanded ? (
                                  <>
                                    <div className="space-y-0.5">
                                      {breakdownData.contributions.map((contrib, idx) => {
                                        const ref = getReference(contrib.citation);
                                        return (
                                          <div key={idx} className="flex items-center gap-1 italic">
                                            <span>+{contrib.displayValue} ({contrib.name})</span>
                                            {ref && (
                                              <a
                                                href={ref.url || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors border border-blue-500/20"
                                                onClick={(e) => !ref.url && e.preventDefault()}
                                              >
                                                <span>[{contrib.citation}]</span>
                                                <span className="max-w-[100px] truncate">{ref.source_name}</span>
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })}
                                      <div className="pt-0.5 border-t border-muted-foreground/20 mt-0.5">
                                        <span className="font-medium">Total: +{breakdownData.total.replace('+', '')} {breakdownData.unit}</span>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setExpandedBreakdowns(prev => {
                                          const next = new Set(prev);
                                          next.delete(metric.key);
                                          return next;
                                        });
                                      }}
                                      className="inline-flex items-center gap-0.5 hover:opacity-70 transition-opacity mt-1"
                                    >{t('ui.text.collapse_breakdown')}<ChevronUp className="h-2.5 w-2.5" />
                                    </button>
                                  </>
                                ) : hasMultipleItems ? (
                                  <div className="inline-flex items-center gap-1">
                                    <span className="italic">{breakdownData.total} {breakdownData.unit}</span>
                                    <span>•</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setExpandedBreakdowns(prev => new Set(prev).add(metric.key));
                                      }}
                                      className="inline-flex items-center gap-0.5 hover:opacity-70 transition-opacity"
                                    >{t('ui.text.see_breakdown')}<ChevronDown className="h-2.5 w-2.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 italic">
                                    <span>{breakdownData.breakdown} {breakdownData.unit}</span>
                                    {(() => {
                                      const contrib = breakdownData.contributions[0];
                                      const ref = getReference(contrib?.citation);
                                      if (!ref) return null;
                                      return (
                                        <a
                                          href={ref.url || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors border border-blue-500/20"
                                          onClick={(e) => !ref.url && e.preventDefault()}
                                        >
                                          <span>[{contrib.citation}]</span>
                                          <span className="max-w-[100px] truncate">{ref.source_name}</span>
                                        </a>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {/* Add Metric Button - Small gray plus button at bottom left */}
                {allMetricsToDisplay.length > 0 && (
                  <motion.div custom={allMetricsToDisplay.length} variants={itemVariants} className="flex justify-start pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAddMetricDialogOpen(true)}
                      className="h-7 px-2 text-[10px] border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 bg-background"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {t('components.quick_entry_confirm.add_metric_button')}
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Healthier Alternative - show if AI provided suggestions and not all metrics are boolean */}
            {!allMetricsAreBoolean && healthierReason && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className={`flex items-center justify-between gap-2 w-full text-[10px] px-2 py-1.5 rounded-lg border transition-colors mt-2 ${enableHealthierSuggestions
                  ? 'bg-yellow-500/5 border-yellow-500/20'
                  : 'bg-transparent border-muted'
                  }`}>
                <div className="flex items-start gap-1.5 flex-1 min-w-0">
                  <span className="text-[14px] flex-shrink-0 mt-[-1px]">💡</span>
                  <div className="flex-1 min-w-0">
                    {healthierReason && enableHealthierSuggestions ? (
                      <span className={`italic ${enableHealthierSuggestions ? 'text-yellow-700/80' : 'text-muted-foreground'}`}>
                        {healthierReason}
                      </span>
                    ) : (
                      <span className={`font-medium ${enableHealthierSuggestions ? 'text-yellow-700/80' : 'text-muted-foreground'}`}>
                        {t('components.quick_entry_confirm.healthier_option')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleToggleHealthierSuggestions(!enableHealthierSuggestions)}
                  className="flex-shrink-0 p-1 hover:bg-yellow-500/10 rounded transition-colors"
                  aria-label={enableHealthierSuggestions ? t('components.quick_entry_confirm.hide_healthier_suggestions') : t('components.quick_entry_confirm.show_healthier_suggestions')}
                >
                  {enableHealthierSuggestions ? (
                    <Eye className="h-3.5 w-3.5 text-yellow-700/60" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
              </motion.div>
            )}

            {/* References Section - show if AI provided references (only for authenticated users) */}
            {references && Array.isArray(references) && references.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
                className="w-full text-[10px] px-2 py-1.5 rounded-lg border bg-muted/30 border-muted mt-2"
              >
                <div className="flex items-start gap-1.5">
                  <span className="text-[14px] flex-shrink-0 mt-[-1px]">📚</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground mb-1">{t('ui.text.references')}</div>
                    <ol className="space-y-1 text-muted-foreground">
                      {references.map((ref: any, idx: number) => (
                        <li key={idx} className="flex gap-1">
                          <span className="font-medium">{idx + 1}.</span>
                          <span className="flex-1">
                            {ref.description}
                            {ref.source_name && (
                              <span className="text-muted-foreground/60">
                                {' '}({ref.source_name}
                                {ref.url && (
                                  <>
                                    {' - '}
                                    <a
                                      href={ref.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline hover:text-foreground transition-colors"
                                    >
                                      Link
                                    </a>
                                  </>
                                )})
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Toggle for showing/hiding disabled metrics - only show if user has at least some metrics enabled */}
            {disabledMetricsCount > 0 && hasAnyMetricsEnabled && (
              <div className="flex items-center justify-end gap-1.5 mt-2">
                <Label htmlFor="show-disabled" className="text-[10px] font-normal text-muted-foreground cursor-pointer">
                  {t('components.quick_entry_confirm.activate_metrics', { count: disabledMetricsCount })}
                </Label>
                <Switch
                  id="show-disabled"
                  checked={showDisabledMetrics}
                  onCheckedChange={setShowDisabledMetrics}
                  className="scale-75"
                />
              </div>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="flex-shrink-0 px-6 pb-2">
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md p-3">
                {validationError}{' '}
                {!userProfile?.is_premium && (
                  <>
                    <Link
                      to="/upgrade"
                      className="underline hover:no-underline font-medium"
                      onClick={() => onOpenChange(false)}
                    >{t('ui.text.upgrade_to_pro')}</Link>
                    {' '}for up to 10 custom metrics.
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-shrink-0 pt-4 border-t gap-2">
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                setEditedValues({});
                setRemovedMetrics(new Set());
                setRemovedCustomMetricIndices(new Set());
                setEditedSuggestions(new Map());
                setValidationError(null);
                setEnabledOptionalIndices(new Set());
                setManuallyAddedMetrics([]);
              }}
            >
              {t('common.cancel')}
            </Button>
            {/* Hide Fix in Chat button when only new custom metrics are shown */}
            {!allMetricsAreNewCustom && (
              <Button
                variant="outline"
                onClick={() => {
                  if (onFixInChat) {
                    onFixInChat();
                  }
                }}
                className="gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t('fix_in_chat.button')}
                {freeFixAvailable && (
                  <span className="ml-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-medium rounded-full">
                    {t('fix_in_chat.free')}
                  </span>
                )}
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || allMetricsToDisplay.length === 0}
              className="relative overflow-hidden"
            >
              {/* Progress bar background fill */}
              {saving && (
                <span
                  className="absolute inset-0 bg-primary-foreground/20 origin-left"
                  style={{
                    transform: `scaleX(${saveProgress / 100})`,
                    transition: saveProgress >= 100
                      ? 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)'
                      : 'transform 0.15s linear',
                  }}
                />
              )}
              <span className="relative z-10">
                {saving ? t('common.adding') : t('common.confirm')}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Metrics Dialog */}
      <CustomMetricsDialog
        userId={userId}
        open={customMetricsDialogOpen}
        onOpenChange={(open) => {
          setCustomMetricsDialogOpen(open);
          if (!open) {
            setEditingSuggestion(null);
          }
        }}
        editMode={!!editingSuggestion}
        initialValues={editingSuggestion ? {
          name: editingSuggestion.name,
          emoji: editingSuggestion.emoji,
          unit: editingSuggestion.unit,
          goal: editingSuggestion.goal?.toString() || "100",
          inverse: editingSuggestion.inverse || false,
          metricType: editingSuggestion.type || "numeric",
          isAdditive: editingSuggestion.is_additive ?? true,
        } : undefined}
        existingCustomMetrics={[
          // Include suggested custom metrics that haven't been removed
          ...suggestedCustomMetrics
            .filter((_, idx) => !removedCustomMetricIndices.has(idx))
            .map((suggestion, idx) => {
              const edited = editedSuggestions.get(idx);
              const metric = edited || suggestion;
              return {
                metric_key: `__custom_suggestion_${idx}`,
                metric_name: metric.name,
                metric_emoji: metric.emoji,
                metric_type: metric.type || "numeric",
              };
            }),
          // Include manually added metrics
          ...manuallyAddedMetrics.map((metric) => ({
            metric_key: metric.key,
            metric_name: metric.name,
            metric_emoji: metric.emoji,
            metric_type: metric.type,
          })),
        ]}
        onCreate={(newMetric) => {
          // Add the new metric to the manually added metrics list
          setManuallyAddedMetrics(prev => [...prev, newMetric]);
          // Set a default value in editedValues
          setEditedValues(prev => ({
            ...prev,
            [newMetric.key]: newMetric.value.toString(),
          }));
          // Close the dialog
          setCustomMetricsDialogOpen(false);
        }}
        onSaveEdit={(editedValues) => {
          if (editingSuggestion) {
            const originalType = editingSuggestion.type;
            const newType = editedValues.metricType;

            // If type changed, reset the value appropriately
            let adjustedValue = editingSuggestion.value;
            if (originalType !== newType) {
              if (newType === "boolean") {
                // Converting to boolean: use 1 (true) if original value > 0, else 0 (false)
                adjustedValue = editingSuggestion.value > 0 ? 1 : 0;
              } else {
                // Converting to numeric: use 1 if was true (1), else 0
                adjustedValue = editingSuggestion.value ? 1 : 0;
              }
            }

            // Update the edited suggestions map
            setEditedSuggestions(prev => {
              const newMap = new Map(prev);

              // Parse goal - set to undefined for relative metrics or invalid values
              let goalValue: number | undefined;
              if (editedValues.isRelative || editedValues.metricType === "boolean") {
                goalValue = undefined;
              } else {
                const parsedGoal = parseFloat(editedValues.goal);
                goalValue = isNaN(parsedGoal) ? undefined : parsedGoal;
              }

              newMap.set(editingSuggestion.originalIndex, {
                name: editedValues.name,
                type: editedValues.metricType,
                unit: editedValues.unit,
                emoji: editedValues.emoji,
                goal: goalValue,
                value: adjustedValue,
                inverse: editedValues.inverse,
                is_additive: editedValues.isAdditive ?? true,
                is_relative: editedValues.isRelative ?? false,
              });
              return newMap;
            });

            // Also update editedValues to reflect the type change
            const suggestionKey = `__custom_suggestion_${editingSuggestion.originalIndex}`;
            if (editedValues.metricType !== editingSuggestion.type) {
              setEditedValues(prev => {
                const newValues = { ...prev };
                // Use the current edited value, or fall back to the adjusted value from the suggestion
                const currentValue = prev[suggestionKey] !== undefined ? prev[suggestionKey] : adjustedValue;
                if (editedValues.metricType === "boolean") {
                  // Converting to boolean
                  newValues[suggestionKey] = Number(currentValue) > 0 ? "1" : "0";
                } else {
                  // Converting to numeric
                  newValues[suggestionKey] = Number(currentValue) ? "1" : "0";
                }
                return newValues;
              });
            }
          }
        }}
      />

      {/* Add Metric Dialog */}
      <AddMetricDialog
        open={addMetricDialogOpen}
        onOpenChange={setAddMetricDialogOpen}
        userId={userId}
        allMetrics={allMetrics}
        metricPreferences={metricPreferences}
        customMetrics={customMetrics}
        displayedMetricKeys={new Set([
          ...Object.keys(validatedSuggestedValues).filter(k => !removedMetrics.has(k)),
          ...Object.keys(editedValues).filter(k => !k.startsWith('__manual') && !k.startsWith('__custom_suggestion') && !removedMetrics.has(k)),
          ...metricsWithValues.map((m: any) => m.key).filter((k: string) => !removedMetrics.has(k)),
          ...customMetricsToCreate.map((m: any) => m.key),
          ...manuallyAddedMetrics.map((m: any) => m.key),
        ])}
        manuallyAddedMetrics={manuallyAddedMetrics}
        onAddBuiltInMetric={handleAddBuiltInMetric}
        onAddExistingCustomMetric={handleAddExistingCustomMetric}
        onCreateCustomMetric={handleCreateCustomMetric}
      />

    </>
  );
};
